import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  Box,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  TablePagination,
  TableSortLabel,
  Paper,
  Chip,
  Typography,
  CircularProgress,
  LinearProgress,
  Alert,
  IconButton,
  Tooltip,
  Avatar,
  Tabs,
  Tab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Divider,
  Stack,
  Link as MuiLink,
  TextField,
  Slider,
  FormControl,
  InputLabel,
  Select,
  OutlinedInput,
  MenuItem,
  Checkbox,
  ListItemText,
  ListSubheader,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import ThumbUpIcon from '@mui/icons-material/ThumbUp';
import ThumbDownIcon from '@mui/icons-material/ThumbDown';
import CloseIcon from '@mui/icons-material/Close';
import BlockIcon from '@mui/icons-material/Block';
import PlaceIcon from '@mui/icons-material/Place';
import WorkIcon from '@mui/icons-material/Work';
import BadgeIcon from '@mui/icons-material/Badge';
import SchoolIcon from '@mui/icons-material/School';
import BusinessIcon from '@mui/icons-material/Business';
import PhoneIcon from '@mui/icons-material/Phone';
import SearchIcon from '@mui/icons-material/Search';
import DescriptionIcon from '@mui/icons-material/Description';
import SettingsIcon from '@mui/icons-material/Settings';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import PlaylistAddIcon from '@mui/icons-material/PlaylistAdd';
import PlaylistAddCheckIcon from '@mui/icons-material/PlaylistAddCheck';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import { DomainDataViewer, initDomainGroups } from '@brainpool/profile-components';
import { SettingsPage } from './SettingsPage';
import type { AnonymizedExportProfile } from '../lib/profileExportPdf';
import { buildCandidatePdfHtml } from '../lib/candidatePdfTemplate';
import {
  escapeCsvField,
  getDisplayName,
  sortCandidates,
  toggleSelectedId,
  toggleSelectedIds,
  type CandidateSortField,
  type SortDirection,
} from '../lib/candidateTableUtils';

type ApplicationStatus = 'pending' | 'approved' | 'rejected';

interface Candidate {
  id: number;
  email: string;
  first_name: string | null;
  last_name: string | null;
  pre_approved: number | null;
  application_status: ApplicationStatus | null;
  applied_at: string | null;
  linkedin_connected: number;
  has_cv: number;
  linkedin_eea_eligible: number | null;
  candidate_summary: string | null;
  profile_picture_url: string | null;
  account_blocked: number | null;
  account_blocked_at: string | null;
  account_block_reason: string | null;
  professional_headline: string | null;
  city: string | null;
  country: string | null;
  years_of_experience: number | null;
  linkedin_url: string | null;
  phone_number: string | null;
  profile_metadata_json: string | null;
  linkedin_data_json: string | null;
  rejection_reason: string | null;
  last_sign_in_at: string | null;
  backlog_project_item_id: string | null;
  backlog_project_item_url: string | null;
  created_at: string;
  updated_at: string;
}

interface RankedCandidateMatch {
  id: number;
  matchScore: number;
}

type PageTab = 'home' | 'search' | 'settings';
type StatusTabKey = 'pending' | 'approved' | 'rejected' | 'applying';

const STATUS_COLOR: Record<ApplicationStatus, 'warning' | 'success' | 'error'> = {
  pending: 'warning',
  approved: 'success',
  rejected: 'error',
};

const SEARCH_RESULTS_LIMIT = 20;
const HOME_STATUS_TABLE_ROWS_PER_PAGE = 20;

const buildAdminHeaders = (): Record<string, string> => {
  const headers: Record<string, string> = { 'content-type': 'application/json' };
  const devEmail = (import.meta.env as Record<string, string>)['VITE_DEV_ADMIN_EMAIL'];
  if (devEmail) headers['x-dev-admin-email'] = devEmail;
  return headers;
};

const getInitials = (c: Candidate) => {
  const name = getDisplayName(c);
  if (name) {
    return name
      .split(' ')
      .map(n => n[0])
      .slice(0, 2)
      .join('')
      .toUpperCase();
  }
  return c.email[0]?.toUpperCase() ?? '?';
};

const getCandidateExportFileStem = (candidate: Candidate): string => {
  return `candidate-${candidate.id}-anonymized-profile`;
};

const downloadBlob = (blob: Blob, fileName: string) => {
  const objectUrl = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = objectUrl;
  anchor.download = fileName;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(objectUrl);
};

const toSearchableText = (c: Candidate): string =>
  [
    getDisplayName(c) ?? '',
    c.email,
    c.professional_headline ?? '',
    c.candidate_summary ?? '',
    c.city ?? '',
    c.country ?? '',
    c.linkedin_url ?? '',
  ]
    .join(' ')
    .toLowerCase();

// ── Levenshtein search helpers ────────────────────────────────────────────────

function levenshtein(a: string, b: string): number {
  if (a.length === 0) return b.length;
  if (b.length === 0) return a.length;
  const dp: number[] = Array.from({ length: b.length + 1 }, (_, i) => i);
  for (let i = 1; i <= a.length; i++) {
    let prev = dp[0];
    dp[0] = i;
    for (let j = 1; j <= b.length; j++) {
      const tmp = dp[j];
      dp[j] = a[i - 1] === b[j - 1] ? prev : 1 + Math.min(prev, dp[j], dp[j - 1]);
      prev = tmp;
    }
  }
  return dp[b.length];
}

function scoreField(query: string, field: string): number {
  if (!field) return 0;
  const q = query.toLowerCase();
  const f = field.toLowerCase();
  if (f === q) return 100;
  if (f.includes(q)) return 92;
  if (q.includes(f) && f.length >= 3) return 85;
  const words = f.split(/[\s,\-/]+/).filter(w => w.length >= 2);
  let best = 0;
  for (const word of words) {
    if (word === q) {
      best = 95;
      break;
    }
    if (word.startsWith(q) || q.startsWith(word)) {
      best = Math.max(best, 80);
    }
    const maxLen = Math.max(q.length, word.length);
    if (maxLen > 0) {
      const sim = Math.round((1 - levenshtein(q, word) / maxLen) * 75);
      best = Math.max(best, sim);
    }
  }
  if (best > 0) return best;
  const maxLen = Math.max(q.length, f.length);
  return Math.max(0, Math.round((1 - levenshtein(q, f) / maxLen) * 60));
}

function extractCandidateFields(c: Candidate): string[] {
  const fields: string[] = [];
  if (c.first_name) fields.push(c.first_name);
  if (c.last_name) fields.push(c.last_name);
  const fullName = [c.first_name, c.last_name].filter(Boolean).join(' ');
  if (fullName) fields.push(fullName);
  fields.push(c.email);
  if (c.linkedin_url) fields.push(c.linkedin_url);
  if (c.city) fields.push(c.city);
  if (c.country) fields.push(c.country);
  if (c.professional_headline) fields.push(c.professional_headline);

  const cvData = safeParseJson<Record<string, unknown>>(c.profile_metadata_json);
  const cvProfileData =
    cvData &&
    typeof cvData.cvProfileData === 'object' &&
    !Array.isArray(cvData.cvProfileData) &&
    cvData.cvProfileData
      ? (cvData.cvProfileData as Record<string, unknown>)
      : null;
  if (cvProfileData) {
    for (const key of ['industry', 'location', 'mostRecentJobTitle', 'highestEducationLevel']) {
      const val = cvProfileData[key];
      if (typeof val === 'string' && val.trim()) fields.push(val.trim());
    }
  }
  if (cvData) {
    const cvObjects = cvData.cvObjects;
    if (Array.isArray(cvObjects)) {
      for (const obj of cvObjects) {
        if (obj && typeof obj === 'object' && !Array.isArray(obj)) {
          const record = obj as Record<string, unknown>;
          for (const key of [
            'title',
            'company',
            'institution',
            'degree',
            'location',
            'fieldOfStudy',
            'school',
          ]) {
            const val = record[key];
            if (typeof val === 'string' && val.trim()) fields.push(val.trim());
          }
        }
      }
    }
  }

  const linkedinData = safeParseJson<Record<string, unknown>>(c.linkedin_data_json);
  if (linkedinData) {
    const positions = linkedinData['positions'] ?? linkedinData['experience'];
    if (Array.isArray(positions)) {
      for (const pos of positions) {
        if (pos && typeof pos === 'object' && !Array.isArray(pos)) {
          const p = pos as Record<string, unknown>;
          for (const key of ['title', 'companyName', 'company', 'location', 'locationName']) {
            const val = p[key];
            if (typeof val === 'string' && val.trim()) fields.push(val.trim());
          }
        }
      }
    }
    const educations = linkedinData['educations'] ?? linkedinData['education'];
    if (Array.isArray(educations)) {
      for (const edu of educations) {
        if (edu && typeof edu === 'object' && !Array.isArray(edu)) {
          const e = edu as Record<string, unknown>;
          for (const key of ['schoolName', 'degreeName', 'fieldOfStudy', 'school']) {
            const val = e[key];
            if (typeof val === 'string' && val.trim()) fields.push(val.trim());
          }
        }
      }
    }
  }

  return Array.from(new Set(fields.filter(Boolean)));
}

const CV_PROFILE_ALLOWED_INDUSTRIES = [
  'Agriculture',
  'Retail & Consumer Goods',
  'Insurance',
  'Healthcare',
  'Financial Services',
  'Gaming',
  'Business Intelligence',
  'Consumer Experience',
  'Legal',
  'R & D',
  'Marketing',
  'Media & Advertising',
  'Market Research',
  'Pharmaceutical Services',
  'Strategy & Planning',
  'Human Resources',
  'Supply Chain & Logistics',
  'Sports',
  'IoT',
  'Smart Cities',
  'Fashion',
  'Virtual Reality',
  'Augmented Reality',
  'Automotive',
] as const;

const CV_PROFILE_ALLOWED_INDUSTRY_LOOKUP = new Map(
  CV_PROFILE_ALLOWED_INDUSTRIES.map(value => [value.toLowerCase(), value])
);

const safeParseJson = <T,>(value: string | null | undefined): T | null => {
  if (!value) {
    return null;
  }
  try {
    return JSON.parse(value) as T;
  } catch {
    return null;
  }
};

const getCandidateIndustry = (candidate: Candidate): string | null => {
  const profileMetadata = safeParseJson<Record<string, unknown>>(candidate.profile_metadata_json);
  const cvProfileData = profileMetadata?.cvProfileData;
  if (!cvProfileData || typeof cvProfileData !== 'object' || Array.isArray(cvProfileData)) {
    return null;
  }

  const industry = (cvProfileData as Record<string, unknown>).industry;
  if (typeof industry !== 'string') {
    return null;
  }

  const normalized = industry.trim().toLowerCase();
  return CV_PROFILE_ALLOWED_INDUSTRY_LOOKUP.get(normalized) ?? null;
};

type CandidateCvProfileData = {
  industry: string | null;
  location: string | null;
  yearsOfExperience: number | null;
  mostRecentJobTitle: string | null;
  highestEducationLevel: string | null;
};

const toNonEmptyString = (value: unknown): string | null => {
  if (typeof value !== 'string') {
    return null;
  }

  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
};

const toFiniteNumber = (value: unknown): number | null => {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }

  if (typeof value === 'string') {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }

  return null;
};

const getCandidateCvProfileData = (candidate: Candidate): CandidateCvProfileData | null => {
  const profileMetadata = safeParseJson<Record<string, unknown>>(candidate.profile_metadata_json);
  const cvProfileData = profileMetadata?.cvProfileData;
  if (!cvProfileData || typeof cvProfileData !== 'object' || Array.isArray(cvProfileData)) {
    return null;
  }

  const data = cvProfileData as Record<string, unknown>;
  const parsed: CandidateCvProfileData = {
    industry: toNonEmptyString(data.industry),
    location: toNonEmptyString(data.location),
    yearsOfExperience: toFiniteNumber(data.yearsOfExperience),
    mostRecentJobTitle: toNonEmptyString(data.mostRecentJobTitle),
    highestEducationLevel: toNonEmptyString(data.highestEducationLevel),
  };

  if (
    parsed.industry === null &&
    parsed.location === null &&
    parsed.yearsOfExperience === null &&
    parsed.mostRecentJobTitle === null &&
    parsed.highestEducationLevel === null
  ) {
    return null;
  }

  return parsed;
};

const TABLE_HEAD_SX = {
  fontWeight: 700,
  bgcolor: 'grey.50',
  borderBottom: '2px solid #E2E2E2',
} as const;

const getCandidateYearsOfExperience = (candidate: Candidate): number | null =>
  candidate.years_of_experience ?? getCandidateCvProfileData(candidate)?.yearsOfExperience ?? null;

const downloadCandidatesNameEmailCsv = (rows: Candidate[], fileStem: string) => {
  const lines = [
    'Name,Email',
    ...rows.map(row => `${escapeCsvField(getDisplayName(row) ?? '')},${escapeCsvField(row.email)}`),
  ];
  const blob = new Blob([`\uFEFF${lines.join('\n')}\n`], { type: 'text/csv;charset=utf-8' });
  downloadBlob(blob, `${fileStem}.csv`);
};

const formatTableDate = (value: string | null | undefined): string => {
  if (!value) {
    return '—';
  }
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? '—' : parsed.toLocaleDateString();
};

const formatTableDateTime = (value: string | null | undefined): string => {
  if (!value) {
    return '—';
  }
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return '—';
  }
  return parsed.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const csvFileStamp = (): string => new Date().toISOString().slice(0, 10);

function SortableHeaderCell({
  label,
  field,
  sortField,
  sortDirection,
  onSort,
  align,
  width,
}: {
  label: string;
  field: CandidateSortField;
  sortField: CandidateSortField;
  sortDirection: SortDirection;
  onSort: (field: CandidateSortField) => void;
  align?: 'left' | 'center' | 'right';
  width?: number;
}) {
  return (
    <TableCell
      align={align}
      sortDirection={sortField === field ? sortDirection : false}
      sx={{ ...TABLE_HEAD_SX, width }}
    >
      <TableSortLabel
        active={sortField === field}
        direction={sortField === field ? sortDirection : 'asc'}
        onClick={() => onSort(field)}
      >
        {label}
      </TableSortLabel>
    </TableCell>
  );
}

// Interpolates the gradient color at a given score (0–100).
// Stops: #e53935 at 0%, #ffc107 at 50%, #43a047 at 100%.
function scoreColor(pct: number): string {
  if (pct <= 50) {
    const t = pct / 50;
    const r = Math.round(229 + (255 - 229) * t);
    const g = Math.round(57 + (193 - 57) * t);
    const b = Math.round(53 + (7 - 53) * t);
    return `rgb(${r},${g},${b})`;
  }
  const t = (pct - 50) / 50;
  const r = Math.round(255 + (67 - 255) * t);
  const g = Math.round(193 + (160 - 193) * t);
  const b = Math.round(7 + (71 - 7) * t);
  return `rgb(${r},${g},${b})`;
}

function MatchScoreGauge({ score }: { score: number }) {
  const pct = Math.max(0, Math.min(100, score));
  const bgWidth = pct > 0 ? `${(100 / pct) * 100}%` : '100%';
  const labelColor = scoreColor(pct);
  return (
    <Box sx={{ width: 64, mx: 'auto' }}>
      <Box
        sx={{
          height: 5,
          borderRadius: 3,
          bgcolor: 'grey.200',
          overflow: 'hidden',
          position: 'relative',
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            left: 0,
            top: 0,
            height: '100%',
            width: `${pct}%`,
            background: 'linear-gradient(to right, #e53935, #ffc107 50%, #43a047)',
            backgroundSize: `${bgWidth} 100%`,
            backgroundRepeat: 'no-repeat',
            borderRadius: 3,
          }}
        />
      </Box>
      <Typography
        variant="caption"
        sx={{
          color: labelColor,
          fontSize: 10,
          display: 'block',
          textAlign: 'center',
          mt: 0.5,
          lineHeight: 1,
          fontWeight: 700,
          letterSpacing: '0.02em',
        }}
      >
        {pct}%
      </Typography>
    </Box>
  );
}

// ── Profile modal ─────────────────────────────────────────────────────────────

function CandidateModal({
  candidate,
  apiUrl,
  onClose,
  onExport,
  isExporting,
  onChangeStatus,
  onDelete,
  onViewCv,
  onParseCv,
  onEmailUnfinished,
  onAddToBacklog,
  backlogEnabled,
  isUpdating,
  isDeleting,
  isViewingCv,
  isParsingCv,
  isEmailingUnfinished,
  isAddingToBacklog,
}: {
  candidate: Candidate;
  apiUrl: string;
  onClose: () => void;
  onExport: (candidate: Candidate) => Promise<void>;
  isExporting: boolean;
  onChangeStatus?: (candidate: Candidate, action: 'approved' | 'rejected') => void;
  onDelete?: (candidate: Candidate) => void;
  onViewCv?: (candidate: Candidate) => void;
  onParseCv?: (candidate: Candidate) => void;
  onEmailUnfinished?: (candidate: Candidate) => void;
  onAddToBacklog?: (candidate: Candidate) => void;
  backlogEnabled?: boolean;
  isUpdating?: boolean;
  isDeleting?: boolean;
  isViewingCv?: boolean;
  isParsingCv?: boolean;
  isEmailingUnfinished?: boolean;
  isAddingToBacklog?: boolean;
}) {
  const name = getDisplayName(candidate);
  const location = [candidate.city, candidate.country].filter(Boolean).join(', ');
  const isBlocked = Boolean(candidate.account_blocked);
  const cvProfileData = getCandidateCvProfileData(candidate);
  const resolvedLocation = cvProfileData?.location ?? location;
  const resolvedYearsOfExperience = getCandidateYearsOfExperience(candidate);
  const domainGroups = initDomainGroups(
    candidate.profile_metadata_json,
    candidate.linkedin_data_json
  );
  const shouldShowProfileDataSection = Boolean(
    candidate.profile_metadata_json || candidate.linkedin_data_json || candidate.has_cv
  );
  const hasParsedProfileData = domainGroups.length > 0;
  const showIncompleteActions = Boolean(candidate.has_cv) && !hasParsedProfileData;
  const showEmailUnfinished = Boolean(onEmailUnfinished) && candidate.application_status === null;
  const actionsBusy = Boolean(
    isExporting ||
    isUpdating ||
    isDeleting ||
    isViewingCv ||
    isParsingCv ||
    isEmailingUnfinished ||
    isAddingToBacklog
  );
  const backlogUrl = candidate.backlog_project_item_url;
  const [lastSignInAt, setLastSignInAt] = useState<string | null | undefined>(
    candidate.last_sign_in_at ?? undefined
  );
  const [lastSignInNote, setLastSignInNote] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLastSignInAt(candidate.last_sign_in_at ?? undefined);
    setLastSignInNote(null);

    const loadSession = async () => {
      try {
        const headers = buildAdminHeaders();
        delete headers['content-type'];
        const res = await fetch(`${apiUrl}/admin/candidates/${candidate.id}/session`, {
          headers,
          credentials: 'include',
        });
        if (!res.ok) {
          const body = (await res.json().catch(() => ({}))) as { error?: string };
          throw new Error(body.error ?? `HTTP ${res.status}`);
        }
        const data = (await res.json()) as {
          ok: boolean;
          lastSignInAt: string | null;
          clerkStatus?: string;
          clerkWarning?: string | null;
        };
        if (cancelled) return;
        setLastSignInAt(data.lastSignInAt);
        if (!data.lastSignInAt && data.clerkStatus === 'skipped') {
          setLastSignInNote(data.clerkWarning ?? 'Login history unavailable');
        } else if (!data.lastSignInAt && data.clerkStatus === 'error') {
          setLastSignInNote(data.clerkWarning ?? 'Login history unavailable');
        } else if (!data.lastSignInAt) {
          setLastSignInNote('No sign-in recorded yet');
        } else {
          setLastSignInNote(null);
        }
      } catch {
        if (!cancelled) {
          setLastSignInAt(null);
          setLastSignInNote('Could not load last login');
        }
      }
    };

    void loadSession();
    return () => {
      cancelled = true;
    };
  }, [apiUrl, candidate.id]);

  return (
    <Dialog open onClose={onClose} maxWidth="md" fullWidth scroll="paper">
      <DialogTitle sx={{ pb: 1 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            <Avatar
              src={candidate.profile_picture_url ?? undefined}
              sx={{ width: 56, height: 56, fontSize: 20, bgcolor: 'primary.light' }}
            >
              {getInitials(candidate)}
            </Avatar>
            <Box>
              <Typography variant="h6" sx={{ lineHeight: 1.2 }}>
                {name ?? <em style={{ color: '#9e9e9e' }}>No name</em>}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {candidate.email}
              </Typography>
              {candidate.professional_headline && (
                <Typography variant="caption" color="text.secondary">
                  {candidate.professional_headline}
                </Typography>
              )}
            </Box>
          </Box>
          <IconButton size="small" onClick={onClose}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
      </DialogTitle>

      <Divider />

      <DialogContent sx={{ pt: 2 }}>
        {/* Status + applied */}
        <Stack direction="row" spacing={1} sx={{ mb: 2 }} flexWrap="wrap" useFlexGap>
          {candidate.application_status ? (
            <Chip
              label={candidate.application_status}
              size="small"
              color={STATUS_COLOR[candidate.application_status]}
            />
          ) : (
            <Chip label="applying" size="small" variant="outlined" color="default" />
          )}
          {candidate.applied_at && (
            <Chip
              label={`Applied ${new Date(candidate.applied_at).toLocaleDateString()}`}
              size="small"
              variant="outlined"
            />
          )}
          {isBlocked && (
            <Chip
              icon={<BlockIcon />}
              label={`Blocked${candidate.account_blocked_at ? ` ${new Date(candidate.account_blocked_at).toLocaleDateString()}` : ''}`}
              size="small"
              color="error"
              variant="outlined"
            />
          )}
        </Stack>

        {/* Info rows */}
        <Stack spacing={1} sx={{ mb: 2 }}>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            <EventAvailableIcon fontSize="small" sx={{ color: 'text.disabled' }} />
            <Typography variant="body2">
              Signed up {new Date(candidate.created_at).toLocaleString()}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            <AccessTimeIcon fontSize="small" sx={{ color: 'text.disabled' }} />
            <Typography variant="body2">
              {lastSignInAt === undefined
                ? 'Last login…'
                : lastSignInAt
                  ? `Last login ${new Date(lastSignInAt).toLocaleString()}`
                  : (lastSignInNote ?? 'Last login unavailable')}
            </Typography>
          </Box>
          {resolvedLocation && (
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              <PlaceIcon fontSize="small" sx={{ color: 'text.disabled' }} />
              <Typography variant="body2">{resolvedLocation}</Typography>
            </Box>
          )}
          {resolvedYearsOfExperience != null && (
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              <WorkIcon fontSize="small" sx={{ color: 'text.disabled' }} />
              <Typography variant="body2">{resolvedYearsOfExperience} years experience</Typography>
            </Box>
          )}
          {cvProfileData?.industry && (
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              <BusinessIcon fontSize="small" sx={{ color: 'text.disabled' }} />
              <Typography variant="body2">{cvProfileData.industry}</Typography>
            </Box>
          )}
          {cvProfileData?.mostRecentJobTitle && (
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              <BadgeIcon fontSize="small" sx={{ color: 'text.disabled' }} />
              <Typography variant="body2">{cvProfileData.mostRecentJobTitle}</Typography>
            </Box>
          )}
          {cvProfileData?.highestEducationLevel && (
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              <SchoolIcon fontSize="small" sx={{ color: 'text.disabled' }} />
              <Typography variant="body2">{cvProfileData.highestEducationLevel}</Typography>
            </Box>
          )}
          {candidate.phone_number && (
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              <PhoneIcon fontSize="small" sx={{ color: 'text.disabled' }} />
              <Typography variant="body2">{candidate.phone_number}</Typography>
            </Box>
          )}
          {candidate.linkedin_url && (
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              <LinkedInIcon fontSize="small" sx={{ color: '#0077b5' }} />
              <MuiLink
                href={candidate.linkedin_url}
                target="_blank"
                rel="noopener noreferrer"
                variant="body2"
              >
                LinkedIn profile
              </MuiLink>
            </Box>
          )}
        </Stack>

        {/* Summary */}
        {candidate.candidate_summary && (
          <>
            <Divider sx={{ mb: 2 }} />
            <Typography
              variant="caption"
              fontWeight={700}
              color="text.secondary"
              sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}
            >
              Candidate summary
            </Typography>
            <Typography variant="body2" sx={{ mt: 0.75, lineHeight: 1.7 }}>
              {candidate.candidate_summary}
            </Typography>
          </>
        )}

        {isBlocked && candidate.account_block_reason && (
          <>
            <Divider sx={{ my: 2 }} />
            <Alert severity="error" sx={{ fontSize: 12 }}>
              <strong>Block reason:</strong> {candidate.account_block_reason}
            </Alert>
          </>
        )}

        {/* Profile data */}
        {shouldShowProfileDataSection && (
          <>
            <Divider sx={{ my: 2 }} />
            <Typography
              variant="caption"
              fontWeight={700}
              color="text.secondary"
              sx={{ textTransform: 'uppercase', letterSpacing: 0.5, display: 'block', mb: 1.5 }}
            >
              Profile data
            </Typography>

            {hasParsedProfileData ? (
              <DomainDataViewer domainGroups={domainGroups} showEmpty={false} />
            ) : candidate.has_cv ? (
              <Alert
                severity="warning"
                sx={{ fontSize: 12 }}
                action={
                  <Stack direction="row" spacing={0.5} sx={{ alignItems: 'center' }}>
                    {onViewCv && (
                      <Button
                        color="inherit"
                        size="small"
                        disabled={actionsBusy}
                        onClick={() => onViewCv(candidate)}
                      >
                        {isViewingCv ? 'Opening…' : 'View CV'}
                      </Button>
                    )}
                    {onParseCv && (
                      <Button
                        color="inherit"
                        size="small"
                        disabled={actionsBusy}
                        onClick={() => onParseCv(candidate)}
                      >
                        {isParsingCv ? 'Parsing…' : 'Parse CV'}
                      </Button>
                    )}
                  </Stack>
                }
              >
                CV file is uploaded, but parsed profile data is not available yet. Parse the CV, or
                email the candidate to complete their profile.
              </Alert>
            ) : (
              <Alert severity="info" sx={{ fontSize: 12 }}>
                No CV profile data is available for this candidate yet.
              </Alert>
            )}
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ flexWrap: 'wrap', gap: 1 }}>
        {onDelete && (
          <Button
            size="small"
            variant="outlined"
            color="error"
            disabled={actionsBusy}
            startIcon={<DeleteOutlineIcon fontSize="small" />}
            onClick={() => onDelete(candidate)}
            sx={{ mr: 'auto' }}
          >
            Delete user
          </Button>
        )}
        {Boolean(candidate.has_cv) && onViewCv && (
          <Button
            size="small"
            variant="outlined"
            disabled={actionsBusy}
            startIcon={<OpenInNewIcon fontSize="small" />}
            onClick={() => onViewCv(candidate)}
          >
            {isViewingCv ? 'Opening…' : 'View CV'}
          </Button>
        )}
        {Boolean(candidate.has_cv) && onParseCv && (
          <Button
            size="small"
            variant={showIncompleteActions ? 'contained' : 'outlined'}
            disabled={actionsBusy}
            startIcon={<AutoAwesomeIcon fontSize="small" />}
            onClick={() => onParseCv(candidate)}
          >
            {isParsingCv ? 'Parsing…' : 'Parse CV'}
          </Button>
        )}
        {showEmailUnfinished && (
          <Button
            size="small"
            variant="outlined"
            disabled={actionsBusy}
            startIcon={<MailOutlineIcon fontSize="small" />}
            onClick={() => onEmailUnfinished?.(candidate)}
          >
            {isEmailingUnfinished ? 'Sending…' : 'Email to complete'}
          </Button>
        )}
        {onChangeStatus && candidate.application_status === 'rejected' && (
          <Button
            size="small"
            variant="outlined"
            color="success"
            disabled={actionsBusy}
            onClick={() => onChangeStatus(candidate, 'approved')}
          >
            Change to Accepted
          </Button>
        )}
        {onChangeStatus && candidate.application_status === 'approved' && (
          <Button
            size="small"
            variant="outlined"
            color="error"
            disabled={actionsBusy}
            onClick={() => onChangeStatus(candidate, 'rejected')}
          >
            Change to Rejected
          </Button>
        )}
        {onChangeStatus && candidate.application_status === 'pending' && (
          <>
            <Button
              size="small"
              variant="outlined"
              color="success"
              disabled={actionsBusy}
              onClick={() => onChangeStatus(candidate, 'approved')}
            >
              Approve
            </Button>
            <Button
              size="small"
              variant="outlined"
              color="error"
              disabled={actionsBusy}
              onClick={() => onChangeStatus(candidate, 'rejected')}
            >
              Reject
            </Button>
          </>
        )}
        {backlogUrl ? (
          <Button
            size="small"
            variant="outlined"
            color="success"
            component="a"
            href={backlogUrl}
            target="_blank"
            rel="noopener noreferrer"
            startIcon={<PlaylistAddCheckIcon fontSize="small" />}
          >
            On backlog
          </Button>
        ) : (
          backlogEnabled &&
          onAddToBacklog && (
            <Button
              size="small"
              variant="outlined"
              disabled={actionsBusy}
              startIcon={<PlaylistAddIcon fontSize="small" />}
              onClick={() => onAddToBacklog(candidate)}
            >
              {isAddingToBacklog ? 'Adding…' : 'Add to backlog'}
            </Button>
          )
        )}
        <Button
          onClick={() => {
            void onExport(candidate);
          }}
          size="small"
          variant="outlined"
          startIcon={<FileDownloadIcon fontSize="small" />}
          disabled={isExporting || actionsBusy}
        >
          {isExporting ? 'Exporting…' : 'Export anonymized PDF'}
        </Button>
        <Button onClick={onClose} size="small" disabled={isDeleting}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ── Main dashboard ────────────────────────────────────────────────────────────

const TAB_FILTER: Record<StatusTabKey, (c: Candidate) => boolean> = {
  applying: c => !c.application_status,
  pending: c => c.application_status === 'pending',
  approved: c => c.application_status === 'approved',
  rejected: c => c.application_status === 'rejected',
};

const STATUS_TAB_ORDER: StatusTabKey[] = ['pending', 'approved', 'rejected', 'applying'];

const STATUS_TAB_LABEL: Record<StatusTabKey, string> = {
  pending: 'Applied',
  approved: 'Accepted',
  rejected: 'Reject',
  applying: 'Applying',
};

const SEARCH_APPLICATION_STATUS_LABEL: Record<StatusTabKey, string> = {
  pending: 'Applied',
  approved: 'Accepted',
  rejected: 'Rejected',
  applying: 'Applying',
};

export function CandidatesDashboard() {
  const [candidates, setCandidates] = useState<Candidate[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [updatingId, setUpdatingId] = useState<number | null>(null);
  const [activePage, setActivePage] = useState<PageTab>('home');
  const [activeStatusTab, setActiveStatusTab] = useState<StatusTabKey>('pending');
  const [selectedCandidate, setSelectedCandidate] = useState<Candidate | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [searching, setSearching] = useState(false);
  const [searchResults, setSearchResults] = useState<Candidate[]>([]);
  const [searchResultScores, setSearchResultScores] = useState<Record<number, number>>({});
  const [searchError, setSearchError] = useState<string | null>(null);
  const [searchedOnce, setSearchedOnce] = useState(false);
  const [searchMode, setSearchMode] = useState<'levenshtein' | 'smart' | null>(null);
  const [searchPage, setSearchPage] = useState(0);
  const [homePageByStatus, setHomePageByStatus] = useState<Record<StatusTabKey, number>>({
    applying: 0,
    pending: 0,
    approved: 0,
    rejected: 0,
  });
  const [experienceRange, setExperienceRange] = useState<[number, number]>([0, 40]);
  const [selectedApplicationStatuses, setSelectedApplicationStatuses] = useState<StatusTabKey[]>([
    'approved',
  ]);
  const [selectedIndustries, setSelectedIndustries] = useState<string[]>([]);
  const [industryOptionsQuery, setIndustryOptionsQuery] = useState('');
  const [isIndustryFilterOpen, setIsIndustryFilterOpen] = useState(false);
  const [isApplicationStatusFilterOpen, setIsApplicationStatusFilterOpen] = useState(false);
  const [confirmCandidate, setConfirmCandidate] = useState<Candidate | null>(null);
  const [confirmAction, setConfirmAction] = useState<'approved' | 'rejected' | null>(null);
  const [confirmFromStatus, setConfirmFromStatus] = useState<ApplicationStatus | null>(null);
  const [rejectionReason, setRejectionReason] = useState('');
  const [defaultRejectionReason, setDefaultRejectionReason] = useState('');
  const [deleteCandidateTarget, setDeleteCandidateTarget] = useState<Candidate | null>(null);
  const [deletingId, setDeletingId] = useState<number | null>(null);
  const [exportingId, setExportingId] = useState<number | null>(null);
  const [viewingCvId, setViewingCvId] = useState<number | null>(null);
  const [parsingCvId, setParsingCvId] = useState<number | null>(null);
  const [addingToBacklogId, setAddingToBacklogId] = useState<number | null>(null);
  // Set from the candidates response; false when the API has no Projects token.
  const [backlogEnabled, setBacklogEnabled] = useState(false);
  const [emailingUnfinishedId, setEmailingUnfinishedId] = useState<number | null>(null);
  const [actionNotice, setActionNotice] = useState<string | null>(null);
  const [selectedHomeCandidateIds, setSelectedHomeCandidateIds] = useState<number[]>([]);
  const [homeSort, setHomeSort] = useState<{ field: CandidateSortField; direction: SortDirection }>(
    {
      field: 'applied',
      direction: 'desc',
    }
  );
  const [searchSort, setSearchSort] = useState<{
    field: CandidateSortField;
    direction: SortDirection;
  }>({
    field: 'applied',
    direction: 'desc',
  });
  const [isBatchEmailingUnfinished, setIsBatchEmailingUnfinished] = useState(false);
  const [confirmBatchEmailOpen, setConfirmBatchEmailOpen] = useState(false);
  const [cohortExportCandidateIds, setCohortExportCandidateIds] = useState<number[]>([]);
  const [isCohortExporting, setIsCohortExporting] = useState(false);
  const [cohortExportProgress, setCohortExportProgress] = useState<{
    completed: number;
    total: number;
    phase: 'idle' | 'generating' | 'zipping';
  }>({ completed: 0, total: 0, phase: 'idle' });

  const apiUrl = ((import.meta.env as Record<string, string>)['VITE_API_URL'] ?? '').replace(
    /\/+$/,
    ''
  );

  const fetchCandidateSessions = useCallback(async () => {
    try {
      const headers = buildAdminHeaders();
      delete headers['content-type'];
      const res = await fetch(`${apiUrl}/admin/candidates/sessions`, {
        headers,
        credentials: 'include',
      });
      if (!res.ok) {
        return;
      }
      const data = (await res.json()) as {
        ok: boolean;
        sessions?: Array<{ id: number; lastSignInAt: string | null }>;
      };
      const byId = new Map(
        (data.sessions ?? []).map(session => [session.id, session.lastSignInAt] as const)
      );
      const applyLastSignIns = (rows: Candidate[]): Candidate[] => {
        let changed = false;
        const next = rows.map(candidate => {
          if (!byId.has(candidate.id)) {
            return candidate;
          }
          const lastSignInAt = byId.get(candidate.id) ?? null;
          if (candidate.last_sign_in_at === lastSignInAt) {
            return candidate;
          }
          changed = true;
          return { ...candidate, last_sign_in_at: lastSignInAt };
        });
        return changed ? next : rows;
      };
      setCandidates(applyLastSignIns);
      setSearchResults(applyLastSignIns);
    } catch {
      // Last login is non-critical for the table.
    }
  }, [apiUrl]);

  const fetchCandidates = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${apiUrl}/admin/candidates`, {
        headers: buildAdminHeaders(),
        credentials: 'include',
      });
      if (!res.ok) {
        const body = (await res.json().catch(() => ({}))) as { error?: string };
        throw new Error(body.error ?? `HTTP ${res.status}`);
      }
      const data = (await res.json()) as {
        ok: boolean;
        candidates: Candidate[];
        backlogEnabled?: boolean;
      };
      setCandidates(
        (data.candidates ?? []).map(candidate => ({
          ...candidate,
          last_sign_in_at: candidate.last_sign_in_at ?? null,
        }))
      );
      setBacklogEnabled(Boolean(data.backlogEnabled));
      void fetchCandidateSessions();
    } catch (err) {
      setError(String(err));
    } finally {
      setLoading(false);
    }
  }, [apiUrl, fetchCandidateSessions]);

  useEffect(() => {
    fetchCandidates();
  }, [fetchCandidates]);

  // Deep link support for the "Open in admin" link on a backlog card:
  // /?candidate=123 opens that candidate once the list has loaded.
  const deepLinkHandledRef = useRef(false);
  useEffect(() => {
    if (deepLinkHandledRef.current || candidates.length === 0) {
      return;
    }

    deepLinkHandledRef.current = true;
    const requestedId = Number(new URLSearchParams(window.location.search).get('candidate'));
    if (!Number.isInteger(requestedId) || requestedId <= 0) {
      return;
    }

    const match = candidates.find(candidate => candidate.id === requestedId);
    if (match) {
      setSelectedCandidate(match);
    } else {
      setError(`Candidate #${requestedId} was not found.`);
    }
  }, [candidates]);

  useEffect(() => {
    const fetchDefaultRejectionReason = async () => {
      try {
        const res = await fetch(`${apiUrl}/admin/settings`, {
          headers: buildAdminHeaders(),
          credentials: 'include',
        });
        if (!res.ok) return;
        const data = (await res.json()) as { ok: boolean; settings: Record<string, string> };
        setDefaultRejectionReason(data.settings['default_rejection_reason'] ?? '');
      } catch {
        // non-critical – confirmation modal will show an empty field as fallback
      }
    };
    void fetchDefaultRejectionReason();
  }, [apiUrl]);

  const candidatesById = useMemo(
    () => new Map(candidates.map(candidate => [candidate.id, candidate])),
    [candidates]
  );

  const selectedCohortCandidateIdSet = useMemo(
    () => new Set(cohortExportCandidateIds),
    [cohortExportCandidateIds]
  );

  const selectedCohortCandidates = useMemo(
    () =>
      cohortExportCandidateIds
        .map(id => candidatesById.get(id))
        .filter((candidate): candidate is Candidate => candidate !== undefined),
    [candidatesById, cohortExportCandidateIds]
  );

  const selectedHomeCandidateIdSet = useMemo(
    () => new Set(selectedHomeCandidateIds),
    [selectedHomeCandidateIds]
  );

  const selectedApplyingEmailCandidates = useMemo(
    () =>
      selectedHomeCandidateIds
        .map(id => candidatesById.get(id))
        .filter(
          (candidate): candidate is Candidate =>
            candidate !== undefined && TAB_FILTER.applying(candidate)
        ),
    [selectedHomeCandidateIds, candidatesById]
  );

  useEffect(() => {
    setCohortExportCandidateIds(prev => {
      const next = prev.filter(id => candidatesById.has(id));
      if (next.length === prev.length && next.every((id, index) => id === prev[index])) {
        return prev;
      }
      return next;
    });
  }, [candidatesById]);

  useEffect(() => {
    setSelectedHomeCandidateIds(prev => {
      const next = prev.filter(id => candidatesById.has(id));
      if (next.length === prev.length && next.every((id, index) => id === prev[index])) {
        return prev;
      }
      return next;
    });
  }, [candidatesById]);

  useEffect(() => {
    setHomePageByStatus({ applying: 0, pending: 0, approved: 0, rejected: 0 });
  }, [homeSort.direction, homeSort.field]);

  useEffect(() => {
    setSearchPage(0);
  }, [searchSort.direction, searchSort.field]);

  useEffect(() => {
    if (activeStatusTab !== 'applying') {
      setConfirmBatchEmailOpen(false);
    }
  }, [activeStatusTab]);

  const candidateIndustriesById = useMemo(() => {
    const map = new Map<number, string | null>();
    for (const candidate of candidates) {
      map.set(candidate.id, getCandidateIndustry(candidate));
    }
    return map;
  }, [candidates]);

  const experienceBounds = useMemo(() => {
    const values = candidates
      .map(candidate => candidate.years_of_experience)
      .filter((value): value is number => typeof value === 'number' && Number.isFinite(value));

    if (values.length === 0) {
      return { min: 0, max: 40 };
    }

    return {
      min: Math.floor(Math.min(...values)),
      max: Math.ceil(Math.max(...values)),
    };
  }, [candidates]);

  useEffect(() => {
    setExperienceRange(([prevMin, prevMax]) => {
      const clampedMin = Math.max(experienceBounds.min, Math.min(prevMin, experienceBounds.max));
      const clampedMax = Math.max(clampedMin, Math.min(prevMax, experienceBounds.max));
      return [clampedMin, clampedMax];
    });
  }, [experienceBounds.max, experienceBounds.min]);

  const hardFilteredCandidates = useMemo(() => {
    const selectedApplicationStatusSet = new Set(selectedApplicationStatuses);
    const selectedIndustrySet = new Set(selectedIndustries);
    const isFullExperienceRange =
      experienceRange[0] === experienceBounds.min && experienceRange[1] === experienceBounds.max;

    return candidates.filter(candidate => {
      const candidateStatus = candidate.application_status ?? 'applying';
      const hasApplicationStatusFilter = selectedApplicationStatusSet.size > 0;
      const passesApplicationStatus =
        !hasApplicationStatusFilter || selectedApplicationStatusSet.has(candidateStatus);

      const candidateIndustry = candidateIndustriesById.get(candidate.id) ?? null;
      const hasIndustryFilter = selectedIndustrySet.size > 0;
      const passesIndustry =
        !hasIndustryFilter ||
        (candidateIndustry !== null && selectedIndustrySet.has(candidateIndustry));

      const years = candidate.years_of_experience;
      const passesExperience =
        typeof years === 'number' && Number.isFinite(years)
          ? years >= experienceRange[0] && years <= experienceRange[1]
          : isFullExperienceRange;

      return passesApplicationStatus && passesIndustry && passesExperience;
    });
  }, [
    candidateIndustriesById,
    candidates,
    experienceBounds.max,
    experienceBounds.min,
    experienceRange,
    selectedApplicationStatuses,
    selectedIndustries,
  ]);

  const hasDefaultApplicationStatusFilterSelection =
    selectedApplicationStatuses.length === 1 && selectedApplicationStatuses[0] === 'approved';

  const hasActiveHardFilters =
    !hasDefaultApplicationStatusFilterSelection ||
    selectedIndustries.length > 0 ||
    experienceRange[0] !== experienceBounds.min ||
    experienceRange[1] !== experienceBounds.max;

  const visibleIndustryOptions = useMemo(() => {
    const query = industryOptionsQuery.trim().toLowerCase();
    if (!query) {
      return CV_PROFILE_ALLOWED_INDUSTRIES;
    }

    return CV_PROFILE_ALLOWED_INDUSTRIES.filter(industry => industry.toLowerCase().includes(query));
  }, [industryOptionsQuery]);

  const hardFilteredCandidateIdsKey = useMemo(
    () => hardFilteredCandidates.map(candidate => candidate.id).join(','),
    [hardFilteredCandidates]
  );
  const previousHardFilteredCandidateIdsRef = useRef<string | null>(null);

  const localSearchCandidates = useCallback(
    (query: string): Candidate[] => {
      const terms = query.trim().toLowerCase().split(/\s+/).filter(Boolean);

      if (terms.length === 0) {
        return [];
      }

      return hardFilteredCandidates.filter(candidate => {
        const industry = candidateIndustriesById.get(candidate.id) ?? '';
        const haystack = `${toSearchableText(candidate)} ${industry}`.toLowerCase();
        return terms.every(term => haystack.includes(term));
      });
    },
    [candidateIndustriesById, hardFilteredCandidates]
  );

  const runSearch = useCallback(async () => {
    const query = searchQuery.trim();
    if (!query) {
      setSearchError(null);
      setSearchResults(hardFilteredCandidates);
      setSearchResultScores({});
      setSearchedOnce(false);
      setSearchMode(null);
      return;
    }

    setSearching(true);
    setSearchError(null);
    setSearchedOnce(true);
    setSearchMode('smart');

    try {
      const res = await fetch(`${apiUrl}/candidates/rerank`, {
        method: 'POST',
        headers: buildAdminHeaders(),
        credentials: 'include',
        body: JSON.stringify({
          query,
          topN: SEARCH_RESULTS_LIMIT,
          candidateIds: hardFilteredCandidates.map(candidate => candidate.id),
        }),
      });

      if (!res.ok) {
        const body = (await res.json().catch(() => ({}))) as { error?: string };
        throw new Error(body.error ?? `HTTP ${res.status}`);
      }

      const data = (await res.json()) as {
        rankedCandidates?: RankedCandidateMatch[];
        rankedIds?: number[];
      };
      const candidatesById = new Map(
        hardFilteredCandidates.map(candidate => [candidate.id, candidate])
      );
      const rankedMatches: Candidate[] = [];
      const matchScoresById: Record<number, number> = {};

      for (const rankedCandidate of data.rankedCandidates ?? []) {
        const id = Number(rankedCandidate.id);
        if (!Number.isInteger(id) || matchScoresById[id] !== undefined) {
          continue;
        }

        const candidate = candidatesById.get(id);
        if (!candidate) {
          continue;
        }

        const rawScore = Number(rankedCandidate.matchScore);
        matchScoresById[id] = Number.isFinite(rawScore)
          ? Math.max(0, Math.min(100, Math.round(rawScore)))
          : 0;
        rankedMatches.push(candidate);
      }

      if (rankedMatches.length === 0) {
        for (const value of data.rankedIds ?? []) {
          const id = Number(value);
          if (!Number.isInteger(id) || matchScoresById[id] !== undefined) {
            continue;
          }
          const candidate = candidatesById.get(id);
          if (!candidate) {
            continue;
          }
          matchScoresById[id] = 0;
          rankedMatches.push(candidate);
        }
      }

      const localMatches = localSearchCandidates(query);
      const seenIds = new Set<number>();
      const merged = [...rankedMatches, ...localMatches].filter(candidate => {
        if (seenIds.has(candidate.id)) {
          return false;
        }
        seenIds.add(candidate.id);
        return true;
      });

      setSearchResults(merged.slice(0, SEARCH_RESULTS_LIMIT));
      setSearchResultScores(matchScoresById);
    } catch (err) {
      setSearchResults(localSearchCandidates(query).slice(0, SEARCH_RESULTS_LIMIT));
      setSearchResultScores({});
      setSearchError('Search API unavailable, showing local candidate matches only.');
      console.error('Candidate search failed', err);
    } finally {
      setSearching(false);
    }
  }, [apiUrl, hardFilteredCandidates, localSearchCandidates, searchQuery]);

  const runLevenshteinSearch = useCallback(() => {
    const query = searchQuery.trim();
    if (!query) {
      setSearchError(null);
      setSearchResults(hardFilteredCandidates);
      setSearchResultScores({});
      setSearchedOnce(false);
      setSearchMode(null);
      setSearchPage(0);
      return;
    }

    setSearchError(null);
    setSearchedOnce(true);
    setSearchMode('levenshtein');
    setSearchPage(0);

    const scored: Array<{ candidate: Candidate; score: number }> = [];
    for (const candidate of hardFilteredCandidates) {
      const fields = extractCandidateFields(candidate);
      let best = 0;
      for (const field of fields) {
        const s = scoreField(query, field);
        if (s > best) best = s;
      }
      if (best >= 30) {
        scored.push({ candidate, score: best });
      }
    }

    scored.sort((a, b) => b.score - a.score);

    const results = scored.map(({ candidate }) => candidate);
    const scores: Record<number, number> = {};
    for (const { candidate, score } of scored) {
      scores[candidate.id] = score;
    }

    setSearchResults(results);
    setSearchResultScores(scores);
  }, [hardFilteredCandidates, searchQuery]);

  useEffect(() => {
    if (searchedOnce) {
      return;
    }
    setSearchResults(hardFilteredCandidates);
    setSearchResultScores({});
  }, [hardFilteredCandidates, searchedOnce]);

  useEffect(() => {
    if (!searchQuery.trim()) {
      setSearchPage(0);
    }
  }, [searchQuery]);

  useEffect(() => {
    if (isIndustryFilterOpen || isApplicationStatusFilterOpen) {
      return;
    }

    if (previousHardFilteredCandidateIdsRef.current === null) {
      previousHardFilteredCandidateIdsRef.current = hardFilteredCandidateIdsKey;
      return;
    }

    if (previousHardFilteredCandidateIdsRef.current === hardFilteredCandidateIdsKey) {
      return;
    }

    previousHardFilteredCandidateIdsRef.current = hardFilteredCandidateIdsKey;

    if (!searchedOnce || !searchQuery.trim()) {
      return;
    }

    if (searchMode === 'levenshtein') {
      runLevenshteinSearch();
    } else {
      void runSearch();
    }
  }, [
    hardFilteredCandidateIdsKey,
    isApplicationStatusFilterOpen,
    isIndustryFilterOpen,
    runLevenshteinSearch,
    runSearch,
    searchMode,
    searchQuery,
    searchedOnce,
  ]);

  const updateStatus = async (id: number, status: ApplicationStatus, reason?: string | null) => {
    setUpdatingId(id);
    try {
      const res = await fetch(`${apiUrl}/admin/candidates/${id}/status`, {
        method: 'POST',
        headers: buildAdminHeaders(),
        credentials: 'include',
        body: JSON.stringify({ status, rejectionReason: reason ?? null }),
      });
      if (!res.ok) {
        const body = (await res.json().catch(() => ({}))) as { error?: string };
        throw new Error(body.error ?? `HTTP ${res.status}`);
      }
      setCandidates(prev =>
        prev.map(c =>
          c.id === id ? { ...c, application_status: status, rejection_reason: reason ?? null } : c
        )
      );
      // Keep modal in sync
      setSelectedCandidate(prev =>
        prev?.id === id
          ? { ...prev, application_status: status, rejection_reason: reason ?? null }
          : prev
      );
    } catch (err) {
      setError(String(err));
    } finally {
      setUpdatingId(null);
    }
  };

  const deleteCandidate = async (candidate: Candidate) => {
    setDeletingId(candidate.id);
    setError(null);
    try {
      const res = await fetch(`${apiUrl}/admin/candidates/${candidate.id}`, {
        method: 'DELETE',
        headers: buildAdminHeaders(),
        credentials: 'include',
      });
      if (!res.ok) {
        const body = (await res.json().catch(() => ({}))) as { error?: string };
        throw new Error(body.error ?? `HTTP ${res.status}`);
      }

      setCandidates(prev => prev.filter(c => c.id !== candidate.id));
      setSearchResults(prev => prev.filter(c => c.id !== candidate.id));
      setSearchResultScores(prev => {
        const next = { ...prev };
        delete next[candidate.id];
        return next;
      });
      setCohortExportCandidateIds(prev => prev.filter(id => id !== candidate.id));
      setSelectedCandidate(prev => (prev?.id === candidate.id ? null : prev));
      setDeleteCandidateTarget(null);
    } catch (err) {
      setError(String(err));
    } finally {
      setDeletingId(null);
    }
  };

  const viewCandidateCv = useCallback(
    async (candidate: Candidate) => {
      setViewingCvId(candidate.id);
      setError(null);
      try {
        const headers = buildAdminHeaders();
        delete headers['content-type'];
        const res = await fetch(`${apiUrl}/admin/candidates/${candidate.id}/cv`, {
          headers,
          credentials: 'include',
        });
        if (!res.ok) {
          const body = (await res.json().catch(() => ({}))) as { error?: string };
          throw new Error(body.error ?? `HTTP ${res.status}`);
        }

        const blob = await res.blob();
        const disposition = res.headers.get('content-disposition') ?? '';
        const filenameMatch = /filename="([^"]+)"/.exec(disposition);
        const filename = filenameMatch?.[1] ?? `candidate-${candidate.id}-cv`;
        const contentType = (res.headers.get('content-type') ?? blob.type ?? '').toLowerCase();
        const canPreviewInBrowser =
          contentType.includes('application/pdf') || filename.toLowerCase().endsWith('.pdf');
        const objectUrl = URL.createObjectURL(blob);

        // Word/other non-PDF types: browsers often start a download from window.open()
        // but still return null, which previously also ran the fallback → double download.
        if (canPreviewInBrowser) {
          const opened = window.open(objectUrl, '_blank', 'noopener,noreferrer');
          if (!opened) {
            const anchor = document.createElement('a');
            anchor.href = objectUrl;
            anchor.download = filename;
            document.body.appendChild(anchor);
            anchor.click();
            anchor.remove();
          }
        } else {
          const anchor = document.createElement('a');
          anchor.href = objectUrl;
          anchor.download = filename;
          document.body.appendChild(anchor);
          anchor.click();
          anchor.remove();
        }
        window.setTimeout(() => URL.revokeObjectURL(objectUrl), 60_000);
      } catch (err) {
        setError(String(err));
      } finally {
        setViewingCvId(prev => (prev === candidate.id ? null : prev));
      }
    },
    [apiUrl]
  );

  const parseCandidateCv = useCallback(
    async (candidate: Candidate) => {
      setParsingCvId(candidate.id);
      setError(null);
      setActionNotice(null);
      try {
        const res = await fetch(`${apiUrl}/admin/candidates/${candidate.id}/cv/parse`, {
          method: 'POST',
          headers: buildAdminHeaders(),
          credentials: 'include',
          body: JSON.stringify({}),
        });
        if (!res.ok) {
          const body = (await res.json().catch(() => ({}))) as { error?: string };
          throw new Error(body.error ?? `HTTP ${res.status}`);
        }

        const data = (await res.json()) as {
          ok: boolean;
          objectCount?: number;
          profile?: Partial<Candidate> & { id: number };
        };

        if (data.profile) {
          const patch: Partial<Candidate> = {
            application_status: data.profile.application_status ?? candidate.application_status,
            applied_at: data.profile.applied_at ?? candidate.applied_at,
            profile_metadata_json:
              data.profile.profile_metadata_json ?? candidate.profile_metadata_json,
            professional_headline:
              data.profile.professional_headline ?? candidate.professional_headline,
            city: data.profile.city ?? candidate.city,
            country: data.profile.country ?? candidate.country,
            years_of_experience: data.profile.years_of_experience ?? candidate.years_of_experience,
            phone_number: data.profile.phone_number ?? candidate.phone_number,
            has_cv: 1,
          };

          setCandidates(prev => prev.map(c => (c.id === candidate.id ? { ...c, ...patch } : c)));
          setSelectedCandidate(prev => (prev?.id === candidate.id ? { ...prev, ...patch } : prev));
        }

        setActionNotice(
          `Parsed CV for ${getDisplayName(candidate) ?? candidate.email}` +
            (typeof data.objectCount === 'number' ? ` (${data.objectCount} objects).` : '.')
        );
      } catch (err) {
        setError(String(err));
      } finally {
        setParsingCvId(null);
      }
    },
    [apiUrl]
  );

  const addCandidateToBacklog = useCallback(
    async (candidate: Candidate) => {
      setAddingToBacklogId(candidate.id);
      setError(null);
      setActionNotice(null);
      try {
        const res = await fetch(`${apiUrl}/admin/candidates/${candidate.id}/backlog`, {
          method: 'POST',
          headers: buildAdminHeaders(),
          credentials: 'include',
          body: JSON.stringify({}),
        });
        if (!res.ok) {
          const body = (await res.json().catch(() => ({}))) as { error?: string };
          throw new Error(body.error ?? `HTTP ${res.status}`);
        }

        const data = (await res.json()) as {
          ok: boolean;
          itemId?: string;
          itemUrl?: string;
          alreadyOnBacklog?: boolean;
        };

        const patch: Partial<Candidate> = {
          backlog_project_item_id: data.itemId ?? null,
          backlog_project_item_url: data.itemUrl ?? null,
        };
        setCandidates(prev => prev.map(c => (c.id === candidate.id ? { ...c, ...patch } : c)));
        setSelectedCandidate(prev => (prev?.id === candidate.id ? { ...prev, ...patch } : prev));

        setActionNotice(
          data.alreadyOnBacklog
            ? `${getDisplayName(candidate) ?? candidate.email} is already on the backlog.`
            : `Added ${getDisplayName(candidate) ?? candidate.email} to the backlog.`
        );
      } catch (err) {
        setError(String(err));
      } finally {
        setAddingToBacklogId(null);
      }
    },
    [apiUrl]
  );

  const emailCandidateUnfinished = useCallback(
    async (candidate: Candidate) => {
      setEmailingUnfinishedId(candidate.id);
      setError(null);
      setActionNotice(null);
      try {
        const res = await fetch(`${apiUrl}/admin/candidates/${candidate.id}/email-unfinished`, {
          method: 'POST',
          headers: buildAdminHeaders(),
          credentials: 'include',
          body: JSON.stringify({}),
        });
        if (!res.ok) {
          const body = (await res.json().catch(() => ({}))) as { error?: string };
          throw new Error(body.error ?? `HTTP ${res.status}`);
        }

        setActionNotice(`Sent “complete your application” email to ${candidate.email}.`);
      } catch (err) {
        setError(String(err));
      } finally {
        setEmailingUnfinishedId(null);
      }
    },
    [apiUrl]
  );

  const toggleCandidateForHomeSelection = useCallback((candidateId: number, checked: boolean) => {
    setSelectedHomeCandidateIds(prev => toggleSelectedId(prev, candidateId, checked));
  }, []);

  const toggleAllVisibleHomeSelection = useCallback((checked: boolean, visibleIds: number[]) => {
    setSelectedHomeCandidateIds(prev => toggleSelectedIds(prev, visibleIds, checked));
  }, []);

  const toggleCandidateForCohortExport = useCallback((candidateId: number, checked: boolean) => {
    setCohortExportCandidateIds(prev => toggleSelectedId(prev, candidateId, checked));
  }, []);

  const toggleAllVisibleForCohortExport = useCallback((checked: boolean, visibleIds: number[]) => {
    setCohortExportCandidateIds(prev => toggleSelectedIds(prev, visibleIds, checked));
  }, []);

  const toggleHomeSort = useCallback((field: CandidateSortField) => {
    setHomeSort(prev => ({
      field,
      direction: prev.field === field && prev.direction === 'asc' ? 'desc' : 'asc',
    }));
  }, []);

  const toggleSearchSort = useCallback((field: CandidateSortField) => {
    setSearchSort(prev => ({
      field,
      direction: prev.field === field && prev.direction === 'asc' ? 'desc' : 'asc',
    }));
  }, []);

  const emailSelectedApplyingCandidates = useCallback(async () => {
    if (selectedApplyingEmailCandidates.length === 0 || isBatchEmailingUnfinished) {
      return;
    }

    setIsBatchEmailingUnfinished(true);
    setError(null);
    setActionNotice(null);
    setConfirmBatchEmailOpen(false);

    try {
      const ids = selectedApplyingEmailCandidates.map(candidate => candidate.id);
      const res = await fetch(`${apiUrl}/admin/candidates/email-unfinished`, {
        method: 'POST',
        headers: buildAdminHeaders(),
        credentials: 'include',
        body: JSON.stringify({ ids }),
      });
      if (!res.ok) {
        const body = (await res.json().catch(() => ({}))) as { error?: string };
        throw new Error(body.error ?? `HTTP ${res.status}`);
      }

      const data = (await res.json()) as {
        ok: boolean;
        sentCount: number;
        failedCount: number;
        results: Array<{ id: number; email: string | null; sent: boolean; error: string | null }>;
      };

      const failed = (data.results ?? []).filter(result => !result.sent);
      if (data.failedCount > 0) {
        const sample = failed
          .slice(0, 3)
          .map(result => `${result.email ?? `#${result.id}`}: ${result.error ?? 'failed'}`)
          .join('; ');
        setError(
          `Sent ${data.sentCount} of ${ids.length}. Failed ${data.failedCount}` +
            (sample ? ` — ${sample}` : '')
        );
      }
      if (data.sentCount > 0) {
        setActionNotice(
          `Sent “complete your application” email to ${data.sentCount} candidate${
            data.sentCount === 1 ? '' : 's'
          }.`
        );
        const sentIds = new Set(
          (data.results ?? []).filter(result => result.sent).map(result => result.id)
        );
        setSelectedHomeCandidateIds(prev => prev.filter(id => !sentIds.has(id)));
      }
    } catch (err) {
      setError(String(err));
    } finally {
      setIsBatchEmailingUnfinished(false);
    }
  }, [apiUrl, isBatchEmailingUnfinished, selectedApplyingEmailCandidates]);

  const fetchAnonymizedProfile = useCallback(
    async (candidateId: number): Promise<AnonymizedExportProfile> => {
      const res = await fetch(`${apiUrl}/admin/candidates/${candidateId}/export-profile`, {
        headers: buildAdminHeaders(),
        credentials: 'include',
      });

      if (!res.ok) {
        const body = (await res.json().catch(() => ({}))) as { error?: string };
        throw new Error(body.error ?? `HTTP ${res.status}`);
      }

      const data = (await res.json()) as {
        ok: boolean;
        exportProfile: AnonymizedExportProfile;
      };

      if (!data.ok || !data.exportProfile) {
        throw new Error('Invalid export response');
      }

      return data.exportProfile;
    },
    [apiUrl]
  );

  const exportCandidateProfile = async (candidate: Candidate) => {
    setExportingId(candidate.id);
    setError(null);

    try {
      const exportProfile = await fetchAnonymizedProfile(candidate.id);
      const html = buildCandidatePdfHtml(exportProfile);
      const res = await fetch(`${apiUrl}/admin/render-pdf`, {
        method: 'POST',
        headers: buildAdminHeaders(),
        body: JSON.stringify({ html, filename: `${getCandidateExportFileStem(candidate)}.pdf` }),
      });
      if (!res.ok) throw new Error(`PDF service: ${res.status}`);
      const blob = await res.blob();
      downloadBlob(blob, `${getCandidateExportFileStem(candidate)}.pdf`);
    } catch (err) {
      setError(String(err));
    } finally {
      setExportingId(null);
    }
  };

  const removeCandidateFromCohortExport = useCallback((candidateId: number) => {
    setCohortExportCandidateIds(prev => prev.filter(id => id !== candidateId));
  }, []);

  const exportCohortCandidates = useCallback(async () => {
    if (selectedCohortCandidates.length === 0 || isCohortExporting) {
      return;
    }

    setError(null);
    setIsCohortExporting(true);
    setCohortExportProgress({
      completed: 0,
      total: selectedCohortCandidates.length,
      phase: 'generating',
    });

    try {
      const files: Array<{ name: string; html: string }> = [];

      for (let index = 0; index < selectedCohortCandidates.length; index += 1) {
        const candidate = selectedCohortCandidates[index];
        const exportProfile = await fetchAnonymizedProfile(candidate.id);
        const html = buildCandidatePdfHtml(exportProfile);
        files.push({
          name: `${String(index + 1).padStart(2, '0')}-${getCandidateExportFileStem(candidate)}.pdf`,
          html,
        });

        setCohortExportProgress({
          completed: index + 1,
          total: selectedCohortCandidates.length,
          phase: 'generating',
        });
      }

      setCohortExportProgress(prev => ({ ...prev, phase: 'zipping' }));

      const zipRes = await fetch(`${apiUrl}/admin/render-zip`, {
        method: 'POST',
        headers: buildAdminHeaders(),
        body: JSON.stringify({ files }),
      });
      if (!zipRes.ok) throw new Error(`PDF service: ${zipRes.status}`);
      const zipBlob = await zipRes.blob();

      const timestamp = new Date().toISOString().slice(0, 19).replace(/[:T]/g, '-');
      downloadBlob(zipBlob, `cohort-anonymized-candidates-${timestamp}.zip`);
    } catch (err) {
      setError(String(err));
    } finally {
      setIsCohortExporting(false);
      setCohortExportProgress({ completed: 0, total: 0, phase: 'idle' });
    }
  }, [fetchAnonymizedProfile, isCohortExporting, selectedCohortCandidates]);

  const openConfirmModal = (
    candidate: Candidate,
    action: 'approved' | 'rejected',
    e: React.MouseEvent
  ) => {
    e.stopPropagation();
    if (candidate.application_status === action) {
      return;
    }
    setConfirmCandidate(candidate);
    setConfirmAction(action);
    setConfirmFromStatus(candidate.application_status ?? null);
    setRejectionReason(action === 'rejected' ? defaultRejectionReason : '');
  };

  const openChangeStatusFromModal = (candidate: Candidate, action: 'approved' | 'rejected') => {
    setConfirmCandidate(candidate);
    setConfirmAction(action);
    setConfirmFromStatus(candidate.application_status ?? null);
    setRejectionReason(action === 'rejected' ? defaultRejectionReason : '');
  };

  const handleConfirm = async () => {
    if (!confirmCandidate || !confirmAction) return;
    await updateStatus(
      confirmCandidate.id,
      confirmAction,
      confirmAction === 'rejected' ? rejectionReason : null
    );
    setConfirmCandidate(null);
    setConfirmAction(null);
    setConfirmFromStatus(null);
    setRejectionReason('');
  };

  const handleConfirmClose = () => {
    setConfirmCandidate(null);
    setConfirmAction(null);
    setConfirmFromStatus(null);
    setRejectionReason('');
  };

  const counts: Record<StatusTabKey, number> = {
    applying: candidates.filter(TAB_FILTER.applying).length,
    pending: candidates.filter(TAB_FILTER.pending).length,
    approved: candidates.filter(TAB_FILTER.approved).length,
    rejected: candidates.filter(TAB_FILTER.rejected).length,
  };

  const homeVisibleCandidates = useMemo(
    () =>
      sortCandidates(
        candidates.filter(TAB_FILTER[activeStatusTab]).map(candidate => ({
          ...candidate,
          years_of_experience: getCandidateYearsOfExperience(candidate),
        })),
        homeSort,
        {}
      ),
    [activeStatusTab, candidates, homeSort]
  );
  const isAiRerankSearchActive =
    searchMode === 'smart' && searchedOnce && Boolean(searchQuery.trim());
  const shouldPaginateSearchResults = !isAiRerankSearchActive;
  const searchVisibleCandidates = useMemo(
    () =>
      sortCandidates(
        searchResults.map(candidate => ({
          ...candidate,
          years_of_experience: getCandidateYearsOfExperience(candidate),
        })),
        searchSort,
        searchResultScores
      ),
    [searchResultScores, searchResults, searchSort]
  );
  const maxSearchPage = Math.max(
    0,
    Math.ceil(searchVisibleCandidates.length / SEARCH_RESULTS_LIMIT) - 1
  );
  const effectiveSearchPage = shouldPaginateSearchResults ? Math.min(searchPage, maxSearchPage) : 0;
  const paginatedSearchVisibleCandidates = shouldPaginateSearchResults
    ? searchVisibleCandidates.slice(
        effectiveSearchPage * SEARCH_RESULTS_LIMIT,
        (effectiveSearchPage + 1) * SEARCH_RESULTS_LIMIT
      )
    : searchVisibleCandidates;
  const maxHomePage = Math.max(
    0,
    Math.ceil(homeVisibleCandidates.length / HOME_STATUS_TABLE_ROWS_PER_PAGE) - 1
  );
  const effectiveHomePage = Math.min(homePageByStatus[activeStatusTab] ?? 0, maxHomePage);
  const paginatedHomeVisibleCandidates = homeVisibleCandidates.slice(
    effectiveHomePage * HOME_STATUS_TABLE_ROWS_PER_PAGE,
    (effectiveHomePage + 1) * HOME_STATUS_TABLE_ROWS_PER_PAGE
  );
  const visibleCandidates =
    activePage === 'search' ? paginatedSearchVisibleCandidates : paginatedHomeVisibleCandidates;

  const selectedHomeCandidatesOnTab = homeVisibleCandidates.filter(candidate =>
    selectedHomeCandidateIdSet.has(candidate.id)
  );
  const selectedSearchCandidates = searchVisibleCandidates.filter(candidate =>
    selectedCohortCandidateIdSet.has(candidate.id)
  );
  const tableSort = activePage === 'search' ? searchSort : homeSort;
  const onTableSort = activePage === 'search' ? toggleSearchSort : toggleHomeSort;
  const showStatusColumn = activePage === 'home' && activeStatusTab !== 'applying';
  const tableColumnCount = 7 + (activePage === 'search' ? 1 : 0) + (showStatusColumn ? 1 : 0);
  const downloadSelectionCount =
    activePage === 'search' ? selectedSearchCandidates.length : selectedHomeCandidatesOnTab.length;
  const downloadTotalCount =
    activePage === 'search' ? searchVisibleCandidates.length : homeVisibleCandidates.length;

  const downloadCurrentTableCsv = () => {
    if (activePage === 'search') {
      const rows =
        selectedSearchCandidates.length > 0 ? selectedSearchCandidates : searchVisibleCandidates;
      downloadCandidatesNameEmailCsv(rows, `candidates-search-${csvFileStamp()}`);
      return;
    }
    const rows =
      selectedHomeCandidatesOnTab.length > 0 ? selectedHomeCandidatesOnTab : homeVisibleCandidates;
    downloadCandidatesNameEmailCsv(rows, `candidates-${activeStatusTab}-${csvFileStamp()}`);
  };

  const cohortExportProgressPercent =
    cohortExportProgress.total > 0
      ? Math.round((cohortExportProgress.completed / cohortExportProgress.total) * 100)
      : 0;

  return (
    <Box sx={{ display: 'flex', minHeight: 'calc(100vh - 59px)', bgcolor: 'grey.50' }}>
      {/* Left nav */}
      <Box
        sx={{
          width: { xs: 96, sm: 220 },
          bgcolor: 'background.paper',
          borderRight: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Tabs
          orientation="vertical"
          value={activePage}
          onChange={(_, value: PageTab) => setActivePage(value)}
          sx={{
            '& .MuiTabs-indicator': {
              left: 0,
              right: 'auto',
              width: 3,
              backgroundColor: '#77C9B8',
            },
            '& .MuiTab-root': {
              alignItems: 'flex-start',
              minHeight: 56,
              textTransform: 'none',
              fontWeight: 600,
              px: { xs: 1, sm: 2 },
            },
          }}
        >
          <Tab
            value="home"
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                <span>Home</span>
                <Chip label={counts.pending} size="small" sx={{ height: 18, fontSize: 11 }} />
              </Box>
            }
          />
          <Tab
            value="search"
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                <span>Search</span>
              </Box>
            }
          />
          <Tab
            value="settings"
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                <SettingsIcon sx={{ fontSize: 16 }} />
                <span>Settings</span>
              </Box>
            }
          />
        </Tabs>
      </Box>

      {/* Main content */}
      <Box sx={{ display: 'flex', flexDirection: 'column', flexGrow: 1, minWidth: 0 }}>
        {activePage === 'home' && (
          <Box
            sx={{
              bgcolor: 'background.paper',
              borderBottom: '1px solid',
              borderColor: 'divider',
              px: 3,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Tabs
                value={activeStatusTab}
                onChange={(_, value: StatusTabKey) => setActiveStatusTab(value)}
                sx={{ '& .MuiTab-root': { minHeight: 48, textTransform: 'none', fontWeight: 500 } }}
              >
                {STATUS_TAB_ORDER.map(tab => (
                  <Tab
                    key={tab}
                    value={tab}
                    label={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                        <span>{STATUS_TAB_LABEL[tab]}</span>
                        <Chip
                          label={counts[tab]}
                          size="small"
                          sx={{
                            height: 18,
                            fontSize: 11,
                            fontWeight: 700,
                            bgcolor: activeStatusTab === tab ? 'primary.main' : 'grey.200',
                            color: activeStatusTab === tab ? 'white' : 'text.secondary',
                            '& .MuiChip-label': { px: 0.75 },
                            pointerEvents: 'none',
                          }}
                        />
                      </Box>
                    }
                  />
                ))}
              </Tabs>
              <Tooltip title="Refresh">
                <span>
                  <IconButton onClick={fetchCandidates} disabled={loading} size="small">
                    <RefreshIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
            </Box>
          </Box>
        )}

        <Box sx={{ p: activePage === 'settings' ? 0 : 3, flexGrow: 1 }}>
          {activePage === 'settings' ? (
            <SettingsPage />
          ) : (
            <>
              {error && (
                <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
                  {error}
                </Alert>
              )}
              {actionNotice && (
                <Alert severity="success" onClose={() => setActionNotice(null)} sx={{ mb: 2 }}>
                  {actionNotice}
                </Alert>
              )}

              {activePage === 'search' && (
                <>
                  <Box sx={{ mb: 2, display: 'flex', flexDirection: 'column', gap: 1.25 }}>
                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                      <TextField
                        size="small"
                        value={searchQuery}
                        onChange={e => setSearchQuery(e.target.value)}
                        placeholder="Search candidates"
                        sx={{ minWidth: 320, flexGrow: 1 }}
                        onKeyDown={e => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            runLevenshteinSearch();
                          }
                        }}
                        InputProps={{
                          startAdornment: (
                            <SearchIcon sx={{ fontSize: 18, color: 'text.disabled', mr: 1 }} />
                          ),
                        }}
                      />
                      <Button
                        variant="contained"
                        size="small"
                        onClick={() => {
                          runLevenshteinSearch();
                        }}
                        disabled={searching || !searchQuery.trim()}
                      >
                        Search
                      </Button>
                      <Tooltip title="AI-powered semantic search that ranks candidates by relevance">
                        <span>
                          <Button
                            variant="outlined"
                            size="small"
                            onClick={() => {
                              void runSearch();
                            }}
                            disabled={searching || !searchQuery.trim()}
                          >
                            Smart Search
                          </Button>
                        </span>
                      </Tooltip>
                      <Button
                        variant="text"
                        size="small"
                        onClick={() => {
                          setSearchQuery('');
                          setSearchError(null);
                          setSearchResults(hardFilteredCandidates);
                          setSearchResultScores({});
                          setSearchedOnce(false);
                          setSearchMode(null);
                          setSearchPage(0);
                        }}
                        disabled={searching}
                      >
                        Clear
                      </Button>
                    </Box>

                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                      <Box sx={{ minWidth: 280, px: 1, flexGrow: 1, maxWidth: 420 }}>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ display: 'block', mb: 0.25 }}
                        >
                          Years of experience: {experienceRange[0]} to {experienceRange[1]}
                        </Typography>
                        <Slider
                          value={experienceRange}
                          min={experienceBounds.min}
                          max={experienceBounds.max}
                          step={1}
                          disableSwap
                          valueLabelDisplay="auto"
                          onChange={(_, value) => {
                            if (!Array.isArray(value) || value.length !== 2) {
                              return;
                            }
                            const [min, max] = value;
                            setExperienceRange([min, max]);
                          }}
                          disabled={experienceBounds.min === experienceBounds.max}
                          sx={{
                            py: 0.75,
                            '& .MuiSlider-rail': { height: 3 },
                            '& .MuiSlider-track': { height: 3 },
                            '& .MuiSlider-thumb': {
                              width: 14,
                              height: 14,
                              '&::before': { boxShadow: 'none' },
                            },
                            '& .MuiSlider-valueLabel': { fontSize: 11 },
                          }}
                        />
                      </Box>

                      <FormControl size="small" sx={{ minWidth: 260, flexGrow: 1, maxWidth: 420 }}>
                        <InputLabel id="application-status-filter-label">
                          Application status
                        </InputLabel>
                        <Select
                          labelId="application-status-filter-label"
                          multiple
                          value={selectedApplicationStatuses}
                          onOpen={() => {
                            setIsApplicationStatusFilterOpen(true);
                          }}
                          onClose={() => {
                            setIsApplicationStatusFilterOpen(false);
                          }}
                          onChange={event => {
                            const value = event.target.value;
                            const selectedValues =
                              typeof value === 'string'
                                ? value.split(',')
                                : (value as StatusTabKey[]);
                            const normalizedStatuses = STATUS_TAB_ORDER.filter(status =>
                              selectedValues.includes(status)
                            );
                            setSelectedApplicationStatuses(normalizedStatuses);
                          }}
                          input={<OutlinedInput label="Application status" />}
                          renderValue={selected => {
                            const selectedList = selected as StatusTabKey[];
                            if (selectedList.length === 0) {
                              return 'All statuses';
                            }

                            return (
                              <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                                {selectedList.map(status => (
                                  <Chip
                                    key={status}
                                    label={SEARCH_APPLICATION_STATUS_LABEL[status]}
                                    size="small"
                                    onDelete={() =>
                                      setSelectedApplicationStatuses(prev =>
                                        prev.filter(item => item !== status)
                                      )
                                    }
                                    onMouseDown={event => {
                                      event.stopPropagation();
                                    }}
                                    sx={{
                                      '& .MuiChip-deleteIcon': {
                                        opacity: 0,
                                        transition: 'opacity 0.15s ease',
                                      },
                                      '&:hover .MuiChip-deleteIcon': {
                                        opacity: 1,
                                      },
                                    }}
                                  />
                                ))}
                              </Box>
                            );
                          }}
                        >
                          {STATUS_TAB_ORDER.map(status => (
                            <MenuItem key={status} value={status}>
                              <Checkbox
                                size="small"
                                checked={selectedApplicationStatuses.includes(status)}
                              />
                              <ListItemText primary={SEARCH_APPLICATION_STATUS_LABEL[status]} />
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>

                      <FormControl size="small" sx={{ minWidth: 320, flexGrow: 1, maxWidth: 520 }}>
                        <InputLabel id="industry-filter-label">Industry</InputLabel>
                        <Select
                          labelId="industry-filter-label"
                          multiple
                          value={selectedIndustries}
                          onOpen={() => {
                            setIsIndustryFilterOpen(true);
                            setIndustryOptionsQuery('');
                          }}
                          onClose={() => {
                            setIsIndustryFilterOpen(false);
                            setIndustryOptionsQuery('');
                          }}
                          onChange={event => {
                            const value = event.target.value;
                            setSelectedIndustries(
                              typeof value === 'string' ? value.split(',') : value
                            );
                          }}
                          input={<OutlinedInput label="Industry" />}
                          MenuProps={{
                            autoFocus: false,
                            PaperProps: {
                              sx: { maxHeight: 360 },
                            },
                          }}
                          renderValue={selected => {
                            const selectedList = selected as string[];
                            if (selectedList.length === 0) {
                              return 'All industries';
                            }

                            return (
                              <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                                {selectedList.map(industry => (
                                  <Chip
                                    key={industry}
                                    label={industry}
                                    size="small"
                                    onDelete={() =>
                                      setSelectedIndustries(prev =>
                                        prev.filter(item => item !== industry)
                                      )
                                    }
                                    onMouseDown={event => {
                                      event.stopPropagation();
                                    }}
                                    sx={{
                                      '& .MuiChip-deleteIcon': {
                                        opacity: 0,
                                        transition: 'opacity 0.15s ease',
                                      },
                                      '&:hover .MuiChip-deleteIcon': {
                                        opacity: 1,
                                      },
                                    }}
                                  />
                                ))}
                              </Box>
                            );
                          }}
                        >
                          <ListSubheader>
                            <TextField
                              size="small"
                              autoFocus
                              placeholder="Search industries"
                              fullWidth
                              value={industryOptionsQuery}
                              onChange={event => {
                                setIndustryOptionsQuery(event.target.value);
                              }}
                              onKeyDown={event => {
                                event.stopPropagation();
                              }}
                            />
                          </ListSubheader>

                          {visibleIndustryOptions.length === 0 && (
                            <MenuItem disabled>
                              <ListItemText primary="No industries found" />
                            </MenuItem>
                          )}

                          {visibleIndustryOptions.map(industry => (
                            <MenuItem key={industry} value={industry}>
                              <Checkbox
                                size="small"
                                checked={selectedIndustries.includes(industry)}
                              />
                              <ListItemText primary={industry} />
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>

                      <Button
                        variant="text"
                        size="small"
                        onClick={() => {
                          setExperienceRange([experienceBounds.min, experienceBounds.max]);
                          setSelectedApplicationStatuses(['pending']);
                          setSelectedIndustries([]);
                        }}
                        disabled={searching || !hasActiveHardFilters}
                      >
                        Reset filters
                      </Button>
                    </Box>
                  </Box>

                  {searchError && (
                    <Alert severity="warning" onClose={() => setSearchError(null)} sx={{ mb: 2 }}>
                      {searchError}
                    </Alert>
                  )}
                </>
              )}

              {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}>
                  <CircularProgress />
                </Box>
              ) : (
                <>
                  {(activePage === 'home' || activePage === 'search') && (
                    <Box
                      sx={{
                        mb: 1.5,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: 1,
                        flexWrap: 'wrap',
                      }}
                    >
                      <Typography variant="body2" color="text.secondary">
                        {downloadSelectionCount > 0
                          ? `${downloadSelectionCount} selected`
                          : activePage === 'home' && activeStatusTab === 'applying'
                            ? 'Select candidates to email a reminder or download names and emails'
                            : 'Select candidates to download names and emails'}
                      </Typography>
                      <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                        <Button
                          size="small"
                          variant="outlined"
                          disabled={
                            downloadTotalCount === 0 ||
                            (activePage === 'home' && isBatchEmailingUnfinished) ||
                            (activePage === 'search' && isCohortExporting)
                          }
                          onClick={() => {
                            if (activePage === 'search') {
                              toggleAllVisibleForCohortExport(
                                true,
                                searchVisibleCandidates.map(candidate => candidate.id)
                              );
                              return;
                            }
                            toggleAllVisibleHomeSelection(
                              true,
                              homeVisibleCandidates.map(candidate => candidate.id)
                            );
                          }}
                        >
                          Select all
                        </Button>
                        <Button
                          size="small"
                          variant="text"
                          disabled={
                            downloadSelectionCount === 0 ||
                            (activePage === 'home' && isBatchEmailingUnfinished) ||
                            (activePage === 'search' && isCohortExporting)
                          }
                          onClick={() => {
                            if (activePage === 'search') {
                              setCohortExportCandidateIds([]);
                              return;
                            }
                            setSelectedHomeCandidateIds(prev =>
                              prev.filter(
                                id => !homeVisibleCandidates.some(candidate => candidate.id === id)
                              )
                            );
                          }}
                        >
                          Clear
                        </Button>
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={<FileDownloadIcon fontSize="small" />}
                          disabled={downloadTotalCount === 0}
                          onClick={downloadCurrentTableCsv}
                        >
                          {downloadSelectionCount > 0
                            ? `Download selected (${downloadSelectionCount})`
                            : `Download all (${downloadTotalCount})`}
                        </Button>
                        {activePage === 'home' && activeStatusTab === 'applying' && (
                          <Button
                            size="small"
                            variant="contained"
                            startIcon={<MailOutlineIcon fontSize="small" />}
                            disabled={
                              isBatchEmailingUnfinished ||
                              selectedApplyingEmailCandidates.length === 0
                            }
                            onClick={() => setConfirmBatchEmailOpen(true)}
                          >
                            {isBatchEmailingUnfinished
                              ? 'Sending…'
                              : `Email to complete (${selectedApplyingEmailCandidates.length})`}
                          </Button>
                        )}
                      </Stack>
                    </Box>
                  )}

                  <TableContainer
                    component={Paper}
                    variant="outlined"
                    sx={{ bgcolor: 'background.paper' }}
                  >
                    <Table size="small" stickyHeader>
                      <TableHead>
                        <TableRow>
                          <TableCell
                            padding="checkbox"
                            sx={{
                              bgcolor: 'grey.50',
                              width: 52,
                              borderBottom: '2px solid #E2E2E2',
                            }}
                          >
                            {activePage === 'search' ? (
                              <Checkbox
                                size="small"
                                indeterminate={
                                  paginatedSearchVisibleCandidates.some(c =>
                                    selectedCohortCandidateIdSet.has(c.id)
                                  ) &&
                                  !paginatedSearchVisibleCandidates.every(c =>
                                    selectedCohortCandidateIdSet.has(c.id)
                                  )
                                }
                                checked={
                                  paginatedSearchVisibleCandidates.length > 0 &&
                                  paginatedSearchVisibleCandidates.every(c =>
                                    selectedCohortCandidateIdSet.has(c.id)
                                  )
                                }
                                disabled={
                                  isCohortExporting || paginatedSearchVisibleCandidates.length === 0
                                }
                                onChange={event => {
                                  toggleAllVisibleForCohortExport(
                                    event.target.checked,
                                    paginatedSearchVisibleCandidates.map(c => c.id)
                                  );
                                }}
                                inputProps={{
                                  'aria-label': 'Select all candidates on this page',
                                }}
                              />
                            ) : (
                              <Checkbox
                                size="small"
                                indeterminate={
                                  paginatedHomeVisibleCandidates.some(c =>
                                    selectedHomeCandidateIdSet.has(c.id)
                                  ) &&
                                  !paginatedHomeVisibleCandidates.every(c =>
                                    selectedHomeCandidateIdSet.has(c.id)
                                  )
                                }
                                checked={
                                  paginatedHomeVisibleCandidates.length > 0 &&
                                  paginatedHomeVisibleCandidates.every(c =>
                                    selectedHomeCandidateIdSet.has(c.id)
                                  )
                                }
                                disabled={
                                  isBatchEmailingUnfinished ||
                                  paginatedHomeVisibleCandidates.length === 0
                                }
                                onChange={event => {
                                  toggleAllVisibleHomeSelection(
                                    event.target.checked,
                                    paginatedHomeVisibleCandidates.map(c => c.id)
                                  );
                                }}
                                inputProps={{
                                  'aria-label': 'Select all candidates on this page',
                                }}
                              />
                            )}
                          </TableCell>
                          <SortableHeaderCell
                            label="Candidate"
                            field="name"
                            sortField={tableSort.field}
                            sortDirection={tableSort.direction}
                            onSort={onTableSort}
                            width={220}
                          />
                          <SortableHeaderCell
                            label="Email"
                            field="email"
                            sortField={tableSort.field}
                            sortDirection={tableSort.direction}
                            onSort={onTableSort}
                          />
                          <SortableHeaderCell
                            label="Years exp."
                            field="years"
                            sortField={tableSort.field}
                            sortDirection={tableSort.direction}
                            onSort={onTableSort}
                            align="center"
                            width={110}
                          />
                          <TableCell align="center" sx={{ ...TABLE_HEAD_SX, width: 120 }}>
                            Data source
                          </TableCell>
                          <SortableHeaderCell
                            label="Applied"
                            field="applied"
                            sortField={tableSort.field}
                            sortDirection={tableSort.direction}
                            onSort={onTableSort}
                            width={110}
                          />
                          <SortableHeaderCell
                            label="Last logged in"
                            field="lastLogin"
                            sortField={tableSort.field}
                            sortDirection={tableSort.direction}
                            onSort={onTableSort}
                            width={160}
                          />
                          {activePage === 'search' && (
                            <SortableHeaderCell
                              label="Match score"
                              field="matchScore"
                              sortField={tableSort.field}
                              sortDirection={tableSort.direction}
                              onSort={onTableSort}
                              align="center"
                              width={110}
                            />
                          )}
                          {showStatusColumn && (
                            <TableCell align="center" sx={{ ...TABLE_HEAD_SX, width: 110 }}>
                              Status
                            </TableCell>
                          )}
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {visibleCandidates.length === 0 ? (
                          <TableRow>
                            <TableCell
                              colSpan={tableColumnCount}
                              align="center"
                              sx={{ py: 6, color: 'text.secondary' }}
                            >
                              {activePage === 'search'
                                ? searchedOnce && searchQuery.trim()
                                  ? 'No candidates matched your search.'
                                  : hasActiveHardFilters
                                    ? 'No candidates match the current filters.'
                                    : 'Search candidates by name, job title, company, location, degree, email, or LinkedIn URL. Use Smart Search for AI-powered ranking.'
                                : `No ${STATUS_TAB_LABEL[activeStatusTab].toLowerCase()} candidates`}
                            </TableCell>
                          </TableRow>
                        ) : (
                          visibleCandidates.map(c => {
                            const isBlocked = Boolean(c.account_blocked);
                            const isUpdating = updatingId === c.id;
                            const isApproved = c.application_status === 'approved';
                            const isRejected = c.application_status === 'rejected';
                            const isPreApproved = c.pre_approved === 1 && !isApproved;
                            const isSelectedForCohort = selectedCohortCandidateIdSet.has(c.id);
                            const isSelectedForHome = selectedHomeCandidateIdSet.has(c.id);
                            const yearsOfExperience = getCandidateYearsOfExperience(c);

                            return (
                              <TableRow
                                key={c.id}
                                hover
                                onClick={() => setSelectedCandidate(c)}
                                sx={{
                                  cursor: 'pointer',
                                  opacity: isBlocked ? 0.55 : 1,
                                  '&:hover': { bgcolor: 'grey.50' },
                                }}
                              >
                                {activePage === 'search' ? (
                                  <TableCell
                                    padding="checkbox"
                                    onClick={event => event.stopPropagation()}
                                  >
                                    <Checkbox
                                      size="small"
                                      checked={isSelectedForCohort}
                                      disabled={isCohortExporting}
                                      onChange={event => {
                                        toggleCandidateForCohortExport(c.id, event.target.checked);
                                      }}
                                      inputProps={{
                                        'aria-label': `Select candidate ${getDisplayName(c) ?? c.email}`,
                                      }}
                                    />
                                  </TableCell>
                                ) : (
                                  <TableCell
                                    padding="checkbox"
                                    onClick={event => event.stopPropagation()}
                                  >
                                    <Checkbox
                                      size="small"
                                      checked={isSelectedForHome}
                                      disabled={isBatchEmailingUnfinished}
                                      onChange={event => {
                                        toggleCandidateForHomeSelection(c.id, event.target.checked);
                                      }}
                                      inputProps={{
                                        'aria-label': `Select ${getDisplayName(c) ?? c.email}`,
                                      }}
                                    />
                                  </TableCell>
                                )}

                                {/* Candidate */}
                                <TableCell>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                                    <Avatar
                                      src={c.profile_picture_url ?? undefined}
                                      sx={{
                                        width: 32,
                                        height: 32,
                                        fontSize: 12,
                                        bgcolor: 'primary.light',
                                        flexShrink: 0,
                                      }}
                                    >
                                      {getInitials(c)}
                                    </Avatar>
                                    <Box sx={{ minWidth: 0 }}>
                                      <Typography variant="body2" fontWeight={600} noWrap>
                                        {getDisplayName(c) ?? (
                                          <em style={{ color: '#9e9e9e' }}>No name</em>
                                        )}
                                      </Typography>
                                      {isBlocked && (
                                        <Box
                                          sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}
                                        >
                                          <BlockIcon sx={{ fontSize: 10, color: 'error.main' }} />
                                          <Typography variant="caption" color="error.main">
                                            blocked
                                          </Typography>
                                        </Box>
                                      )}
                                    </Box>
                                  </Box>
                                </TableCell>

                                {/* Email */}
                                <TableCell>
                                  <Typography variant="body2" sx={{ wordBreak: 'break-all' }}>
                                    {c.email}
                                  </Typography>
                                </TableCell>

                                {/* Years of experience */}
                                <TableCell align="center">
                                  <Typography variant="body2" color="text.secondary">
                                    {yearsOfExperience != null ? yearsOfExperience : '—'}
                                  </Typography>
                                </TableCell>

                                {/* Data source */}
                                <TableCell align="center">
                                  {c.has_cv || c.linkedin_url ? (
                                    <Stack
                                      direction="row"
                                      spacing={0.5}
                                      justifyContent="center"
                                      alignItems="center"
                                    >
                                      {c.has_cv ? (
                                        <Tooltip title="View uploaded CV">
                                          <IconButton
                                            size="small"
                                            aria-label="View uploaded CV"
                                            disabled={viewingCvId === c.id}
                                            onClick={event => {
                                              event.stopPropagation();
                                              void viewCandidateCv(c);
                                            }}
                                            sx={{ p: 0.25 }}
                                          >
                                            <DescriptionIcon
                                              fontSize="small"
                                              sx={{ color: 'text.primary' }}
                                            />
                                          </IconButton>
                                        </Tooltip>
                                      ) : null}
                                      {c.linkedin_url ? (
                                        <Tooltip title="LinkedIn profile">
                                          <MuiLink
                                            href={c.linkedin_url}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            sx={{ display: 'flex', color: '#0077b5' }}
                                          >
                                            <LinkedInIcon
                                              fontSize="small"
                                              sx={{ color: '#0077b5' }}
                                            />
                                          </MuiLink>
                                        </Tooltip>
                                      ) : null}
                                    </Stack>
                                  ) : (
                                    <Typography variant="caption" color="text.disabled">
                                      —
                                    </Typography>
                                  )}
                                </TableCell>

                                {/* Applied */}
                                <TableCell>
                                  <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    sx={{ whiteSpace: 'nowrap' }}
                                  >
                                    {formatTableDate(c.applied_at)}
                                  </Typography>
                                </TableCell>

                                {/* Last logged in */}
                                <TableCell>
                                  <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    sx={{ whiteSpace: 'nowrap' }}
                                  >
                                    {formatTableDateTime(c.last_sign_in_at)}
                                  </Typography>
                                </TableCell>

                                {/* Match score */}
                                {activePage === 'search' && (
                                  <TableCell align="center">
                                    {typeof searchResultScores[c.id] === 'number' ? (
                                      <MatchScoreGauge score={searchResultScores[c.id]} />
                                    ) : (
                                      <Typography variant="caption" color="text.disabled">
                                        —
                                      </Typography>
                                    )}
                                  </TableCell>
                                )}

                                {/* Status */}
                                {showStatusColumn && (
                                  <TableCell align="center" onClick={e => e.stopPropagation()}>
                                    <Box
                                      sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}
                                    >
                                      <Tooltip
                                        title={
                                          isApproved
                                            ? 'Accepted'
                                            : isRejected
                                              ? 'Change to Accepted'
                                              : 'Approve'
                                        }
                                      >
                                        <span>
                                          <IconButton
                                            size="small"
                                            disabled={isUpdating || isApproved}
                                            onClick={e => openConfirmModal(c, 'approved', e)}
                                            sx={{
                                              color: isApproved
                                                ? 'success.main'
                                                : 'action.disabled',
                                              '&.Mui-disabled': isApproved
                                                ? { color: 'success.main', opacity: 1 }
                                                : {},
                                              '&:hover': !isApproved
                                                ? { color: 'success.main' }
                                                : {},
                                            }}
                                          >
                                            <ThumbUpIcon fontSize="small" />
                                          </IconButton>
                                        </span>
                                      </Tooltip>
                                      <Tooltip
                                        title={
                                          isRejected
                                            ? 'Rejected'
                                            : isApproved
                                              ? 'Change to Rejected'
                                              : 'Reject'
                                        }
                                      >
                                        <span>
                                          <IconButton
                                            size="small"
                                            disabled={isUpdating || isRejected}
                                            onClick={e => openConfirmModal(c, 'rejected', e)}
                                            sx={{
                                              color: isRejected ? 'error.main' : 'action.disabled',
                                              '&.Mui-disabled': isRejected
                                                ? { color: 'error.main', opacity: 1 }
                                                : {},
                                              '&:hover': !isRejected ? { color: 'error.main' } : {},
                                            }}
                                          >
                                            <ThumbDownIcon fontSize="small" />
                                          </IconButton>
                                        </span>
                                      </Tooltip>
                                    </Box>
                                  </TableCell>
                                )}
                              </TableRow>
                            );
                          })
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>

                  {activePage === 'search' &&
                    shouldPaginateSearchResults &&
                    searchVisibleCandidates.length > SEARCH_RESULTS_LIMIT && (
                      <TablePagination
                        component="div"
                        count={searchVisibleCandidates.length}
                        page={effectiveSearchPage}
                        onPageChange={(_, nextPage) => {
                          setSearchPage(nextPage);
                        }}
                        rowsPerPage={SEARCH_RESULTS_LIMIT}
                        rowsPerPageOptions={[SEARCH_RESULTS_LIMIT]}
                        labelRowsPerPage="Candidates per page"
                        sx={{
                          border: '1px solid',
                          borderColor: 'divider',
                          borderTop: 'none',
                          bgcolor: 'background.paper',
                        }}
                      />
                    )}

                  {activePage === 'home' &&
                    homeVisibleCandidates.length > HOME_STATUS_TABLE_ROWS_PER_PAGE && (
                      <TablePagination
                        component="div"
                        count={homeVisibleCandidates.length}
                        page={effectiveHomePage}
                        onPageChange={(_, nextPage) => {
                          setHomePageByStatus(prev => ({
                            ...prev,
                            [activeStatusTab]: nextPage,
                          }));
                        }}
                        rowsPerPage={HOME_STATUS_TABLE_ROWS_PER_PAGE}
                        rowsPerPageOptions={[HOME_STATUS_TABLE_ROWS_PER_PAGE]}
                        labelRowsPerPage="Candidates per page"
                        sx={{
                          border: '1px solid',
                          borderColor: 'divider',
                          borderTop: 0,
                          borderRadius: '0 0 8px 8px',
                          bgcolor: 'background.paper',
                        }}
                      />
                    )}

                  {activePage === 'search' && (
                    <Box sx={{ mt: 2 }}>
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          gap: 1,
                          mb: 1,
                          flexWrap: 'wrap',
                        }}
                      >
                        <Typography variant="subtitle2" fontWeight={700}>
                          Cohort export candidates ({selectedCohortCandidates.length})
                        </Typography>
                        <Button
                          variant="contained"
                          size="small"
                          startIcon={<FileDownloadIcon fontSize="small" />}
                          disabled={isCohortExporting || selectedCohortCandidates.length === 0}
                          onClick={() => {
                            void exportCohortCandidates();
                          }}
                        >
                          {isCohortExporting ? 'Exporting cohort…' : 'Export anonymized cohort'}
                        </Button>
                      </Box>

                      {isCohortExporting && cohortExportProgress.total > 0 && (
                        <Box sx={{ mb: 1.5 }}>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ mb: 0.5, display: 'block' }}
                          >
                            {cohortExportProgress.phase === 'zipping'
                              ? 'Creating ZIP archive…'
                              : `Generating PDFs ${cohortExportProgress.completed}/${cohortExportProgress.total}`}
                          </Typography>
                          <LinearProgress
                            variant="determinate"
                            value={
                              cohortExportProgress.phase === 'zipping'
                                ? 100
                                : cohortExportProgressPercent
                            }
                          />
                        </Box>
                      )}

                      <TableContainer
                        component={Paper}
                        variant="outlined"
                        sx={{ bgcolor: 'background.paper' }}
                      >
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell sx={{ ...TABLE_HEAD_SX, width: 220 }}>Candidate</TableCell>
                              <TableCell sx={TABLE_HEAD_SX}>Email</TableCell>
                              <TableCell align="center" sx={{ ...TABLE_HEAD_SX, width: 110 }}>
                                Years exp.
                              </TableCell>
                              <TableCell sx={{ ...TABLE_HEAD_SX, width: 110 }}>Applied</TableCell>
                              <TableCell sx={{ ...TABLE_HEAD_SX, width: 160 }}>
                                Last logged in
                              </TableCell>
                              <TableCell align="center" sx={{ ...TABLE_HEAD_SX, width: 80 }}>
                                Remove
                              </TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {selectedCohortCandidates.length === 0 ? (
                              <TableRow>
                                <TableCell
                                  colSpan={6}
                                  align="center"
                                  sx={{ py: 4, color: 'text.secondary' }}
                                >
                                  Select candidates from the search results to build an export
                                  cohort.
                                </TableCell>
                              </TableRow>
                            ) : (
                              selectedCohortCandidates.map(candidate => (
                                <TableRow
                                  key={`cohort-${candidate.id}`}
                                  hover
                                  onClick={() => setSelectedCandidate(candidate)}
                                  sx={{ cursor: 'pointer', '&:hover': { bgcolor: 'grey.50' } }}
                                >
                                  <TableCell>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                                      <Avatar
                                        src={candidate.profile_picture_url ?? undefined}
                                        sx={{
                                          width: 32,
                                          height: 32,
                                          fontSize: 12,
                                          bgcolor: 'primary.light',
                                          flexShrink: 0,
                                        }}
                                      >
                                        {getInitials(candidate)}
                                      </Avatar>
                                      <Typography variant="body2" fontWeight={600} noWrap>
                                        {getDisplayName(candidate) ?? (
                                          <em style={{ color: '#9e9e9e' }}>No name</em>
                                        )}
                                      </Typography>
                                    </Box>
                                  </TableCell>
                                  <TableCell>
                                    <Typography variant="body2" sx={{ wordBreak: 'break-all' }}>
                                      {candidate.email}
                                    </Typography>
                                  </TableCell>
                                  <TableCell align="center">
                                    <Typography variant="body2" color="text.secondary">
                                      {getCandidateYearsOfExperience(candidate) ?? '—'}
                                    </Typography>
                                  </TableCell>
                                  <TableCell>
                                    <Typography
                                      variant="caption"
                                      color="text.secondary"
                                      sx={{ whiteSpace: 'nowrap' }}
                                    >
                                      {formatTableDate(candidate.applied_at)}
                                    </Typography>
                                  </TableCell>
                                  <TableCell>
                                    <Typography
                                      variant="caption"
                                      color="text.secondary"
                                      sx={{ whiteSpace: 'nowrap' }}
                                    >
                                      {formatTableDateTime(candidate.last_sign_in_at)}
                                    </Typography>
                                  </TableCell>
                                  <TableCell
                                    align="center"
                                    onClick={event => event.stopPropagation()}
                                  >
                                    <Tooltip title="Remove from cohort export">
                                      <span>
                                        <IconButton
                                          size="small"
                                          disabled={isCohortExporting}
                                          onClick={() => {
                                            removeCandidateFromCohortExport(candidate.id);
                                          }}
                                        >
                                          <DeleteOutlineIcon fontSize="small" />
                                        </IconButton>
                                      </span>
                                    </Tooltip>
                                  </TableCell>
                                </TableRow>
                              ))
                            )}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </Box>
                  )}
                </>
              )}
            </>
          )}
        </Box>

        {/* Profile modal */}
        {selectedCandidate && (
          <CandidateModal
            candidate={selectedCandidate}
            apiUrl={apiUrl}
            onClose={() => setSelectedCandidate(null)}
            onExport={exportCandidateProfile}
            isExporting={exportingId === selectedCandidate.id}
            onChangeStatus={openChangeStatusFromModal}
            onDelete={candidate => {
              setSelectedCandidate(null);
              setDeleteCandidateTarget(candidate);
            }}
            onViewCv={candidate => {
              void viewCandidateCv(candidate);
            }}
            onParseCv={candidate => {
              void parseCandidateCv(candidate);
            }}
            onEmailUnfinished={candidate => {
              void emailCandidateUnfinished(candidate);
            }}
            onAddToBacklog={candidate => {
              void addCandidateToBacklog(candidate);
            }}
            backlogEnabled={backlogEnabled}
            isAddingToBacklog={addingToBacklogId === selectedCandidate.id}
            isUpdating={updatingId === selectedCandidate.id}
            isDeleting={deletingId === selectedCandidate.id}
            isViewingCv={viewingCvId === selectedCandidate.id}
            isParsingCv={parsingCvId === selectedCandidate.id}
            isEmailingUnfinished={emailingUnfinishedId === selectedCandidate.id}
          />
        )}

        {/* Confirm batch unfinished-application email */}
        {confirmBatchEmailOpen && (
          <Dialog
            open
            onClose={() => {
              if (!isBatchEmailingUnfinished) {
                setConfirmBatchEmailOpen(false);
              }
            }}
            maxWidth="xs"
            fullWidth
          >
            <DialogTitle sx={{ pb: 1 }}>Email selected candidates</DialogTitle>
            <DialogContent>
              <Typography variant="body2">
                Send a “complete your application” reminder to{' '}
                <strong>{selectedApplyingEmailCandidates.length}</strong> selected candidate
                {selectedApplyingEmailCandidates.length === 1 ? '' : 's'}?
              </Typography>
            </DialogContent>
            <DialogActions>
              <Button
                onClick={() => setConfirmBatchEmailOpen(false)}
                size="small"
                disabled={isBatchEmailingUnfinished}
              >
                Cancel
              </Button>
              <Button
                onClick={() => {
                  void emailSelectedApplyingCandidates();
                }}
                size="small"
                variant="contained"
                disabled={isBatchEmailingUnfinished || selectedApplyingEmailCandidates.length === 0}
                startIcon={<MailOutlineIcon fontSize="small" />}
              >
                {isBatchEmailingUnfinished ? 'Sending…' : 'Send emails'}
              </Button>
            </DialogActions>
          </Dialog>
        )}

        {/* Confirm delete modal */}
        {deleteCandidateTarget && (
          <Dialog
            open
            onClose={() => {
              if (deletingId !== deleteCandidateTarget.id) {
                setDeleteCandidateTarget(null);
              }
            }}
            maxWidth="xs"
            fullWidth
          >
            <DialogTitle sx={{ pb: 1 }}>Delete user</DialogTitle>
            <DialogContent>
              <Typography variant="body2">
                Permanently delete{' '}
                <strong>
                  {getDisplayName(deleteCandidateTarget) ?? deleteCandidateTarget.email}
                </strong>{' '}
                and remove all of their profile data, CV uploads, and account records from the
                database? This cannot be undone.
              </Typography>
            </DialogContent>
            <DialogActions>
              <Button
                onClick={() => setDeleteCandidateTarget(null)}
                size="small"
                disabled={deletingId === deleteCandidateTarget.id}
              >
                Cancel
              </Button>
              <Button
                onClick={() => {
                  void deleteCandidate(deleteCandidateTarget);
                }}
                size="small"
                variant="contained"
                color="error"
                disabled={deletingId === deleteCandidateTarget.id}
              >
                {deletingId === deleteCandidateTarget.id ? 'Deleting…' : 'Delete permanently'}
              </Button>
            </DialogActions>
          </Dialog>
        )}

        {/* Confirm action modal */}
        {confirmCandidate && confirmAction && (
          <Dialog open onClose={handleConfirmClose} maxWidth="xs" fullWidth>
            <DialogTitle sx={{ pb: 1 }}>
              {confirmFromStatus === 'approved' || confirmFromStatus === 'rejected'
                ? 'Change application status'
                : confirmAction === 'approved'
                  ? 'Approve candidate'
                  : 'Reject candidate'}
            </DialogTitle>
            <DialogContent>
              <Typography variant="body2" sx={{ mb: confirmAction === 'rejected' ? 2 : 0 }}>
                {confirmFromStatus === 'approved' || confirmFromStatus === 'rejected' ? (
                  <>
                    Are you sure you want to change{' '}
                    <strong>{getDisplayName(confirmCandidate) ?? confirmCandidate.email}</strong>{' '}
                    from{' '}
                    <strong>{confirmFromStatus === 'approved' ? 'Accepted' : 'Rejected'}</strong> to{' '}
                    <strong>{confirmAction === 'approved' ? 'Accepted' : 'Rejected'}</strong>?
                    {confirmAction === 'rejected' && (
                      <> You can edit the rejection reason below before confirming.</>
                    )}
                  </>
                ) : confirmAction === 'approved' ? (
                  <>
                    Approve{' '}
                    <strong>{getDisplayName(confirmCandidate) ?? confirmCandidate.email}</strong>?
                  </>
                ) : (
                  <>
                    Reject{' '}
                    <strong>{getDisplayName(confirmCandidate) ?? confirmCandidate.email}</strong>?
                    You can edit the rejection reason below before confirming.
                  </>
                )}
              </Typography>
              {confirmAction === 'rejected' && (
                <TextField
                  multiline
                  rows={4}
                  fullWidth
                  size="small"
                  label="Rejection reason"
                  value={rejectionReason}
                  onChange={e => setRejectionReason(e.target.value)}
                  placeholder="Enter a rejection reason…"
                />
              )}
            </DialogContent>
            <DialogActions>
              <Button onClick={handleConfirmClose} size="small">
                Cancel
              </Button>
              <Button
                onClick={() => {
                  void handleConfirm();
                }}
                size="small"
                variant="contained"
                color={confirmAction === 'approved' ? 'success' : 'error'}
                disabled={updatingId === confirmCandidate.id}
              >
                {updatingId === confirmCandidate.id
                  ? 'Saving…'
                  : confirmAction === 'approved'
                    ? 'Approve'
                    : 'Reject'}
              </Button>
            </DialogActions>
          </Dialog>
        )}
      </Box>
    </Box>
  );
}
